import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth, ROLES } from '../../hooks/useAuth';
import { ROUTES, APP_NAME, APP_VERSION, FIREBASE_CONFIG } from '../../constants';
import { formatPrice } from '../../utils';
import { calculateCustomerGrandTotal, calculateCustomerRemainingAmount } from '../../utils/customerFinancialUtils';
import { getSupplierSessionRemaining } from '../../utils/supplierSessionBalanceUtils';
import { buildDeliveryReminders, hasDeliveryReminders } from '../../utils/deliveryReminderUtils';
import { exportData, importData, validateBackupFile } from '../../utils/backupUtils';
import CustomerList from '../customers/CustomerList';
import DealerList from '../dealers/DealerList';
import ProductList from '../productList/ProductList';
// import AccountingReport from '../accounting/AccountingReport'; // GEÇİCİ: Hesaplamalar sekmesi devre dışı

import './Dashboard.css';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const EMPTY_DELIVERY_REMINDERS = {
  overdue: [],
  today: [],
  tomorrow: [],
  thisWeek: []
};

const DeliveryReminderGrid = ({ reminders, showDealerMeta = false }) => (
  <div className="delivery-reminders-grid">
    {reminders.overdue.length > 0 && (
      <div className="reminder-card reminder-overdue">
        <div className="reminder-header">
          <i className="fas fa-exclamation-triangle"></i>
          <span>Geciken Teslimatlar</span>
          <span className="reminder-count">{reminders.overdue.length}</span>
        </div>
        <div className="reminder-list">
          {reminders.overdue.slice(0, 5).map(item => (
            <div key={item.id} className="reminder-item">
              <div className="reminder-customer-name">{item.name}</div>
              {showDealerMeta && item.dealerName && (
                <div className="reminder-dealer-name">{item.dealerName}</div>
              )}
              <div className="reminder-details">
                <span className="reminder-days">{item.daysOverdue} gün gecikti</span>
                {item.phone && <span className="reminder-phone"><i className="fas fa-phone"></i> {item.phone}</span>}
              </div>
            </div>
          ))}
          {reminders.overdue.length > 5 && (
            <div className="reminder-more">+{reminders.overdue.length - 5} daha...</div>
          )}
        </div>
      </div>
    )}

    {reminders.today.length > 0 && (
      <div className="reminder-card reminder-today">
        <div className="reminder-header">
          <i className="fas fa-clock"></i>
          <span>Bugün Teslim</span>
          <span className="reminder-count">{reminders.today.length}</span>
        </div>
        <div className="reminder-list">
          {reminders.today.slice(0, 5).map(item => (
            <div key={item.id} className="reminder-item">
              <div className="reminder-customer-name">{item.name}</div>
              {showDealerMeta && item.dealerName && (
                <div className="reminder-dealer-name">{item.dealerName}</div>
              )}
              <div className="reminder-details">
                {item.phone && <span className="reminder-phone"><i className="fas fa-phone"></i> {item.phone}</span>}
                {item.address && <span className="reminder-address"><i className="fas fa-map-marker-alt"></i> {item.address.substring(0, 30)}...</span>}
              </div>
            </div>
          ))}
          {reminders.today.length > 5 && (
            <div className="reminder-more">+{reminders.today.length - 5} daha...</div>
          )}
        </div>
      </div>
    )}

    {reminders.tomorrow.length > 0 && (
      <div className="reminder-card reminder-tomorrow">
        <div className="reminder-header">
          <i className="fas fa-calendar-day"></i>
          <span>Yarın Teslim</span>
          <span className="reminder-count">{reminders.tomorrow.length}</span>
        </div>
        <div className="reminder-list">
          {reminders.tomorrow.slice(0, 5).map(item => (
            <div key={item.id} className="reminder-item">
              <div className="reminder-customer-name">{item.name}</div>
              {showDealerMeta && item.dealerName && (
                <div className="reminder-dealer-name">{item.dealerName}</div>
              )}
              <div className="reminder-details">
                {item.phone && <span className="reminder-phone"><i className="fas fa-phone"></i> {item.phone}</span>}
              </div>
            </div>
          ))}
          {reminders.tomorrow.length > 5 && (
            <div className="reminder-more">+{reminders.tomorrow.length - 5} daha...</div>
          )}
        </div>
      </div>
    )}

    {reminders.thisWeek.length > 0 && (
      <div className="reminder-card reminder-week">
        <div className="reminder-header">
          <i className="fas fa-calendar-week"></i>
          <span>Bu Hafta</span>
          <span className="reminder-count">{reminders.thisWeek.length}</span>
        </div>
        <div className="reminder-list">
          {reminders.thisWeek.slice(0, 5).map(item => (
            <div key={item.id} className="reminder-item">
              <div className="reminder-customer-name">{item.name}</div>
              {showDealerMeta && item.dealerName && (
                <div className="reminder-dealer-name">{item.dealerName}</div>
              )}
              <div className="reminder-details">
                <span className="reminder-days">{item.daysLeft} gün kaldı</span>
                {item.phone && <span className="reminder-phone"><i className="fas fa-phone"></i> {item.phone}</span>}
              </div>
            </div>
          ))}
          {reminders.thisWeek.length > 5 && (
            <div className="reminder-more">+{reminders.thisWeek.length - 5} daha...</div>
          )}
        </div>
      </div>
    )}
  </div>
);

// Her ürün tipine göre sipariş maliyetini doğru hesaplayan yardımcı fonksiyon
export const computeDashboardOrderCost = (order) => {
  if (!order) return 0;

  // 1) Plise
  if (
    order.productType === 'plise' &&
    Array.isArray(order.pliseItems) && order.pliseItems.length > 0 &&
    (parseFloat(order.pliseProduct1Cost) > 0 || parseFloat(order.pliseProduct2Cost) > 0)
  ) {
    const c1 = parseFloat(order.pliseProduct1Cost) || 0;
    const c2 = parseFloat(order.pliseProduct2Cost) || 0;
    const mCost = parseFloat(order.mountingCost) || 0;
    const gQty = parseFloat(order.quantity) || 1;
    let costSum = 0;
    order.pliseItems.forEach(item => {
      const m2 = parseFloat(item.usedM2) || 0;
      const qty = parseFloat(item.quantity) || 1;
      const unitCost = c1 + (item.multisistem ? c2 : 0);
      costSum += m2 * unitCost * qty;
    });
    return (costSum + mCost) * gQty;
  }

  // 2) Tül / Kanat (Toptan/Bayi ve Perakende)
  if (order.productType === 'tul' || order.productType === 'kanat') {
    const savedTotalCost = parseFloat(order.totalCost);
    const uCost = parseFloat(order.unitCost) || parseFloat(order.baseUnitCost) || parseFloat(order.fabricCost) || 0;
    const manualExtra = parseFloat(order.manualExtraCost) || 0;
    const finalUnitCost = uCost + manualExtra;
    const measure = parseFloat(order.usedM2) || parseFloat(order.width) || parseFloat(order.usedMeter) || 0;
    const qty = parseFloat(order.quantity) || 1;
    const mCost = parseFloat(order.mountingCost) || 0;
    const sExtrasCost = parseFloat(order.systemExtrasCostValue) || 0;
    const motorCost = parseFloat(order.motorCost) || parseFloat(order.motorCostValue) || 0;

    const computed = ((measure * finalUnitCost) + sExtrasCost + motorCost + mCost) * qty;

    // Eğer veritabanındaki totalCost > 0 ise ve hesaplanan ile uyumluysa onu kullan
    if (!isNaN(savedTotalCost) && savedTotalCost > 0) {
      return savedTotalCost;
    }
    if (computed > 0) {
      return computed;
    }
    return Math.max(0, savedTotalCost || 0);
  }

  // 3) Stor / Zebra / Jaluzi / Dikey / Katlamalı
  if (['stor', 'zebra', 'ahsapJaluzi', 'metalJaluzi', 'dikeyPerde', 'dikeyPvc', 'dikeyStor', 'katlamaliPerde'].includes(order.productType)) {
    const savedTotalCost = parseFloat(order.totalCost);
    if (!isNaN(savedTotalCost) && savedTotalCost > 0) {
      return savedTotalCost;
    }
    const uCost = parseFloat(order.unitCost) || parseFloat(order.baseUnitCost) || parseFloat(order.fabricCost) || 0;
    const manualExtra = parseFloat(order.manualExtraCost) || 0;
    const finalUnitCost = uCost + manualExtra;
    const measure = parseFloat(order.usedM2) || parseFloat(order.usedMeter) || parseFloat(order.width) || 0;
    const qty = parseFloat(order.quantity) || 1;
    const mCost = parseFloat(order.mountingCost) || 0;
    const sExtrasCost = parseFloat(order.systemExtrasCostValue) || 0;
    const motorCost = parseFloat(order.motorCost) || parseFloat(order.motorCostValue) || 0;

    const computed = ((measure * finalUnitCost) + sExtrasCost + motorCost + mCost) * qty;
    if (computed > 0) {
      return computed;
    }
    return Math.max(0, savedTotalCost || 0);
  }

  // 4) Diğer ürün tipleri (Rustik, Aksesuar, Ray vs.)
  const savedTotalCost = parseFloat(order.totalCost);
  if (!isNaN(savedTotalCost) && savedTotalCost > 0) {
    return savedTotalCost;
  }
  const uCost = parseFloat(order.unitCost) || parseFloat(order.baseUnitCost) || parseFloat(order.fabricCost) || 0;
  const measure = parseFloat(order.usedMeter) || parseFloat(order.usedM2) || parseFloat(order.quantity) || 1;
  const qty = parseFloat(order.quantity) || 1;
  if (uCost > 0) {
    return measure * uCost * (order.usedMeter ? qty : 1);
  }

  return Math.max(0, savedTotalCost || 0);
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, userRole, permissions, isAdmin, isMontajci, isYazilimci, loading } = useAuth();

  // LocalStorage'dan son sekmeyi oku
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('activeTab');
    return savedTab || 'dashboard';
  });

  // Rol değiştiğinde veya yüklendiğinde erişim kontrolü
  useEffect(() => {
    if (permissions) {
      // Satışcı dashboard'a erişemez, customers'a yönlendir
      if (!permissions.canAccessDashboard && activeTab === 'dashboard') {
        setActiveTab('customers');
      }
      // Montajcı sadece customers'a erişebilir
      if (isMontajci && activeTab !== 'customers') {
        setActiveTab('customers');
      }
      // Kullanıcı yönetimine erişim yoksa ve users sekmesindeyse
      if (!isAdmin && activeTab === 'users') {
        setActiveTab('customers');
      }
    }
  }, [permissions, activeTab, isMontajci, isAdmin]);

  // Sekme kontrolü: Perakende veya Bayi
  const [statsTab, setStatsTab] = useState('retail');

  // Caching ve güncelleme bilgisi
  const [statsLastUpdated, setStatsLastUpdated] = useState(null);

  // Perakende İstatistikleri
  const [customerStats, setCustomerStats] = useState({
    total: 0,
    active: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0
  });
  const [revenueStats, setRevenueStats] = useState({
    expectedTotal: 0, // Tüm siparişlerin toplamı (beklenti)
    total: 0,
    totalNetProfit: 0,
    remainingTotal: 0, // Kalan ödemeler toplamı
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0,
    potentialNetProfit: 0,
    todayProfit: 0,
    thisWeekProfit: 0,
    thisMonthProfit: 0,
    thisYearProfit: 0,
    // Ödeme tipine göre günlük gelirler
    todayCash: 0, // Bugün Elden Nakit
    todayIban: 0, // Bugün IBAN
    yesterdayCreditCard: 0 // Dün Kredi Kartı (bir gün gecikme)
  });
  const [lastYearStats, setLastYearStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  });
  const [monthlyOrderTrend, setMonthlyOrderTrend] = useState([]);
  const [monthlyCustomerTrend, setMonthlyCustomerTrend] = useState([]);
  const [monthlyEarningsTrend, setMonthlyEarningsTrend] = useState([]);

  // Bayi İstatistikleri
  const [dealerStats, setDealerStats] = useState({
    total: 0,
    active: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0
  });
  const [dealerRevenueStats, setDealerRevenueStats] = useState({
    totalAmount: 0,
    paidAmount: 0,
    totalNetProfit: 0,
    remainingAmount: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0,
    potentialNetProfit: 0,
    todayProfit: 0,
    thisWeekProfit: 0,
    thisMonthProfit: 0,
    thisYearProfit: 0,
    supplierTotalDebt: 0,
    supplierTotalPurchases: 0,
    supplierTotalPaid: 0
  });
  const [dealerMonthlyOrderTrend, setDealerMonthlyOrderTrend] = useState([]);       // Sipariş listesi (session) açılış trendi
  const [dealerMonthlyOrderItemTrend, setDealerMonthlyOrderItemTrend] = useState([]); // Gerçek sipariş kalemi trendi
  const [dealerMonthlyEarningsTrend, setDealerMonthlyEarningsTrend] = useState([]);
  const [monthlyNetProfitTrend, setMonthlyNetProfitTrend] = useState([]);
  const [dealerMonthlyNetProfitTrend, setDealerMonthlyNetProfitTrend] = useState([]);
  const [deliveryReminders, setDeliveryReminders] = useState(EMPTY_DELIVERY_REMINDERS);
  const [dealerDeliveryReminders, setDealerDeliveryReminders] = useState(EMPTY_DELIVERY_REMINDERS);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  // Döviz kurları — tedarikçi borçlarını TL'ye çevirmek için (ref: useCallback stale closure'dan kaçınmak için)
  const supplierExchangeRatesRef = React.useRef({ USD: 1, EUR: 1 });
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);

  //Firma bilgileri state'leri
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    phone: ''
  });
  const [companyError, setCompanyError] = useState(null);
  const [companySuccess, setCompanySuccess] = useState(false);
  const [deleteCompanyModal, setDeleteCompanyModal] = useState(null); // Silinecek firma bilgisi

  //Şifre değiştirme state'leri
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordConfirmModal, setShowPasswordConfirmModal] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Kullanıcı Yönetimi State'leri
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    password: '',
    displayName: '',
    role: ROLES.SATISCI
  });
  const [userFormError, setUserFormError] = useState(null);
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [deleteUserModal, setDeleteUserModal] = useState(null);

  // Notification Modal State
  const [notificationModal, setNotificationModal] = useState({
    show: false,
    type: 'success', // 'success', 'error', 'warning', 'info'
    title: '',
    message: '',
    details: null
  });

  // Notification Modal göster
  const showNotificationModal = useCallback((type, title, message, details = null) => {
    setNotificationModal({
      show: true,
      type,
      title,
      message,
      details
    });
  }, []);

  // Notification Modal kapat
  const closeNotificationModal = useCallback(() => {
    setNotificationModal({
      show: false,
      type: 'success',
      title: '',
      message: '',
      details: null
    });
  }, []);

  // Keep Alive Tab States (Lazy Mounting)
  const [hasMountedCustomers, setHasMountedCustomers] = useState(false);
  const [hasMountedDealers, setHasMountedDealers] = useState(false);
  const [hasMountedProducts, setHasMountedProducts] = useState(false);
  const [hasMountedUsers, setHasMountedUsers] = useState(false);

  useEffect(() => {
    if (activeTab === 'customers') setHasMountedCustomers(true);
    else if (activeTab === 'dealers') setHasMountedDealers(true);
    else if (activeTab === 'products') setHasMountedProducts(true);
    else if (activeTab === 'users') setHasMountedUsers(true);
  }, [activeTab]);

  // Döviz kurlarını çek (component mount'ta bir kez)
  useEffect(() => {
    fetch('https://open.er-api.com/v6/latest/USD')
      .then(r => r.json())
      .then(data => {
        if (data?.rates) {
          const usdToTry = parseFloat(data.rates.TRY) || 1;
          const eurToTry = data.rates.EUR ? parseFloat(data.rates.TRY) / parseFloat(data.rates.EUR) : 1;
          supplierExchangeRatesRef.current = { USD: usdToTry, EUR: eurToTry };
        }
      })
      .catch(() => { });
  }, []);

  // Tüm istatistikleri çek
  const fetchAllStats = useCallback(async (forceRefresh = false) => {
    try {
      setIsLoadingStats(true);

      // 30 dakikalık cache kontrolü (Firebase Read kotasını korumak için)
      if (!forceRefresh) {
        const cachedData = localStorage.getItem('dashboard_stats_cache');
        const cachedTime = localStorage.getItem('dashboard_stats_time');

        if (cachedData && cachedTime) {
          const ageInMs = Date.now() - parseInt(cachedTime, 10);
          const cacheLimit = 30 * 60 * 1000; // 30 Dakika
          if (ageInMs < cacheLimit) {
            const parsed = JSON.parse(cachedData);
            if (parsed.dealerRevStatsData && parsed.dealerRevStatsData.supplierTotalDebt !== undefined) {
              setCustomerStats(parsed.customerStats);
              setRevenueStats(parsed.revenueStats);
              setLastYearStats(parsed.lastYearStatsTemp);
              setMonthlyOrderTrend(parsed.monthlyTrendData);
              setMonthlyCustomerTrend(parsed.monthlyCustomerTrendData);
              setMonthlyEarningsTrend(parsed.monthlyEarningsTrendData);
              setMonthlyNetProfitTrend(parsed.monthlyNetProfitTrendData);
              setDeliveryReminders(parsed.deliveryData || EMPTY_DELIVERY_REMINDERS);
              setDealerDeliveryReminders(parsed.dealerDeliveryData || EMPTY_DELIVERY_REMINDERS);

              setDealerStats(parsed.dealerStatsData);
              setDealerRevenueStats(parsed.dealerRevStatsData);
              setDealerMonthlyOrderTrend(parsed.dealerMonthlyTrendData);
              setDealerMonthlyOrderItemTrend(parsed.dealerMonthlyOrderItemData);
              setDealerMonthlyEarningsTrend(parsed.dealerMonthlyEarningsData);
              setDealerMonthlyNetProfitTrend(parsed.dealerMonthlyNetProfitData);

              setStatsLastUpdated(new Date(parseInt(cachedTime, 10)));
              setIsLoadingStats(false);
              return;
            }
          }
        }
      }

      // Yazılımcı/Rol kısıtlaması yoksa stats/dashboard birleştirilmiş belgesini (Materialized View) kontrol et
      const isFilteredView = isYazilimci && user?.uid;
      if (!forceRefresh && !isFilteredView) {
        try {
          const statsDocRef = doc(db, 'stats', 'dashboard');
          const statsDocSnap = await getDoc(statsDocRef);
          if (statsDocSnap.exists()) {
            const data = statsDocSnap.data();
            const updatedAt = data.updatedAt || Date.now();
            const ageInMs = Date.now() - updatedAt;
            const docLimit = 4 * 60 * 60 * 1000; // 4 Saat geçerlilik süresi

            if (ageInMs < docLimit) {
              setCustomerStats(data.customerStats);
              setRevenueStats(data.revenueStats);
              setLastYearStats(data.lastYearStatsTemp);
              setMonthlyOrderTrend(data.monthlyTrendData);
              setMonthlyCustomerTrend(data.monthlyCustomerTrendData);
              setMonthlyEarningsTrend(data.monthlyEarningsTrendData);
              setMonthlyNetProfitTrend(data.monthlyNetProfitTrendData);
              setDeliveryReminders(data.deliveryData || EMPTY_DELIVERY_REMINDERS);
              setDealerDeliveryReminders(data.dealerDeliveryData || EMPTY_DELIVERY_REMINDERS);

              setDealerStats(data.dealerStatsData);
              setDealerRevenueStats(data.dealerRevStatsData);
              setDealerMonthlyOrderTrend(data.dealerMonthlyTrendData);
              setDealerMonthlyOrderItemTrend(data.dealerMonthlyOrderItemData);
              setDealerMonthlyEarningsTrend(data.dealerMonthlyEarningsData);
              setDealerMonthlyNetProfitTrend(data.dealerMonthlyNetProfitData);

              setStatsLastUpdated(new Date(updatedAt));

              // Local cache'i de bu veriyle doldur ki sekme geçişlerinde sıfır veritabanı okuması yapılsın
              const dataToCache = {
                customerStats: data.customerStats,
                revenueStats: data.revenueStats,
                lastYearStatsTemp: data.lastYearStatsTemp,
                monthlyTrendData: data.monthlyTrendData,
                monthlyCustomerTrendData: data.monthlyCustomerTrendData,
                monthlyEarningsTrendData: data.monthlyEarningsTrendData,
                monthlyNetProfitTrendData: data.monthlyNetProfitTrendData,
                deliveryData: data.deliveryData,
                dealerDeliveryData: data.dealerDeliveryData,
                dealerStatsData: data.dealerStatsData,
                dealerRevStatsData: data.dealerRevStatsData,
                dealerMonthlyTrendData: data.dealerMonthlyTrendData,
                dealerMonthlyOrderItemData: data.dealerMonthlyOrderItemData,
                dealerMonthlyEarningsData: data.dealerMonthlyEarningsData,
                dealerMonthlyNetProfitData: data.dealerMonthlyNetProfitData
              };
              localStorage.setItem('dashboard_stats_cache', JSON.stringify(dataToCache));
              localStorage.setItem('dashboard_stats_time', updatedAt.toString());

              setIsLoadingStats(false);
              return;
            }
          }
        } catch (docErr) {
          console.warn('Birleştirilmiş istatistik belgesi okunamadı, canlı hesaplamaya geçiliyor:', docErr);
        }
      }

      // Müşterileri çek
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      let customers = customersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Yazılımcı rolü için sadece kendi oluşturduğu müşterileri filtrele
      if (isYazilimci && user?.uid) {
        customers = customers.filter(c => c.createdBy === user.uid);
      }

      // Siparişleri çek
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      let orders = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));


      const now = new Date();
      const todayDelivery = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastYear = now.getFullYear() - 1;
      const lastYearYearStart = new Date(lastYear, 0, 1);
      const lastYearYearEnd = new Date(lastYear, 11, 31, 23, 59, 59, 999);
      const lastYearTodayStart = new Date(lastYear, now.getMonth(), now.getDate());
      const lastYearTodayEnd = new Date(lastYear, now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const lastYearWeekStart = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate() - 6);
      const lastYearWeekEnd = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const lastYearMonthStart = new Date(lastYear, now.getMonth(), 1);
      const lastYearMonthEnd = new Date(lastYear, now.getMonth() + 1, 0, 23, 59, 59, 999);
      const thisYear = new Date(now.getFullYear(), 0, 1);
      const thisYearDealer = new Date(now.getFullYear(), 0, 1);

      // Müşteri istatistikleri
      const customerStats = {
        total: customers.length,
        active: customers.filter(customer => {
          // Sipariş durumu "Yeni Sipariş" olan müşteriler
          return customer.orderStatus === 'new_order';
        }).length,
        today: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bugün (aynı gün kontrolü)
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const customerDate = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());
          return customerDate.getTime() === today.getTime();
        }).length,
        thisWeek: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Son 7 gün (bugün dahil)
          const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
          sevenDaysAgo.setHours(0, 0, 0, 0);
          return orderDate >= sevenDaysAgo;
        }).length,
        thisMonth: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bu ay sipariş veren müşteriler
          return orderDate >= thisMonth;
        }).length,
        thisYear: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bu yıl sipariş veren müşteriler (yılın ilk günü)
          const thisYearStart = new Date(now.getFullYear(), 0, 1);
          return orderDate >= thisYearStart;
        }).length
      };

      // Gelir istatistikleri
      const revenueStats = {
        expectedTotal: 0, // Tüm siparişlerin toplamı (beklenti)
        total: 0,
        totalNetProfit: 0,
        remainingTotal: 0, // Kalan ödemeler toplamı (Atölye + Firma + Montaj + Teslim Edildi)
        today: 0,
        thisWeek: 0,
        thisMonth: 0,
        thisYear: 0,
        potentialNetProfit: 0,
        todayProfit: 0,
        thisWeekProfit: 0,
        thisMonthProfit: 0,
        thisYearProfit: 0,
        // Ödeme tipine göre günlük gelirler
        todayCash: 0,
        todayIban: 0,
        yesterdayCreditCard: 0
      };

      const lastYearStatsTemp = {
        total: 0,
        today: 0,
        thisWeek: 0,
        thisMonth: 0
      };

      // Her müşteri için ödenen tutarları kontrol et (gerçek kazanç)
      customers.forEach(customer => {
        // Müşterinin ödenen tutarını al (gerçek kazanç)
        const paidAmount = parseFloat(customer.paidAmount || 0);

        // Toplam Gelir Beklentisi - Genel Toplam (Net) ile uyumlu hesap
        const customerOrders = orders.filter(order => order.customerId === customer.id);
        const customerTotalRevenue = calculateCustomerGrandTotal(customer, customerOrders);

        if (customerTotalRevenue > 0 || customerOrders.length > 0 || customer.manualTotalOverride != null) {
          revenueStats.expectedTotal += customerTotalRevenue;
        }

        // Toplam Gelir - Ödenen tutarlar (gerçek kazanç)
        revenueStats.total += paidAmount;

        // Toplam Net Gelir - Maliyeti olan ve onaylanmış siparişlere göre hesapla
        let totalCostForCustomerAll = 0;
        let hasValidCostOrderAll = false;
        const validStatusesForProfit = ['atolye', 'firma', 'montaj', 'delivered'];

        customerOrders.forEach(order => {
          const costValue = computeDashboardOrderCost(order);
          totalCostForCustomerAll += costValue;
          if (costValue > 0 || order.totalCost !== undefined || order.unitCost || order.baseUnitCost || order.fabricCost) {
            hasValidCostOrderAll = true;
          }
        });

        if (hasValidCostOrderAll || customerOrders.length > 0) {
          // Potansiyel Net Kazanç: Beklenen Gelir - Toplam Maliyet
          revenueStats.potentialNetProfit += (customerTotalRevenue - totalCostForCustomerAll);

          // Gerçek Net Kazanç: Alınan Ödeme - Toplam Maliyet (Onaylanmış siparişler için)
          if (validStatusesForProfit.includes(customer.orderStatus)) {
            revenueStats.totalNetProfit += (paidAmount - totalCostForCustomerAll);
          }
        }

        // Kalan Ödemeler - Atölye + Firma + Montaj + Teslim Edildi (canlı hesap, Beklenen Gelir ile uyumlu)
        if (['atolye', 'firma', 'montaj', 'delivered'].includes(customer.orderStatus)) {
          const remainingAmount = calculateCustomerRemainingAmount(customer, customerOrders);
          if (remainingAmount > 0) {
            revenueStats.remainingTotal += remainingAmount;
          }
        }

        // Ödeme geçmişinden ödeme tipine göre günlük gelirleri hesapla (sipariş tarihinden bağımsız)
        const paymentHistory = customer.paymentHistory || [];
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

        paymentHistory.forEach(payment => {
          // Firestore Timestamp veya ISO string olabilir
          let paymentDate;
          if (payment.date?.toDate) {
            paymentDate = payment.date.toDate();
          } else if (payment.date?.seconds) {
            paymentDate = new Date(payment.date.seconds * 1000);
          } else {
            paymentDate = new Date(payment.date);
          }
          const paymentDateOnly = new Date(paymentDate.getFullYear(), paymentDate.getMonth(), paymentDate.getDate());

          // Bugün Elden Nakit
          if (payment.type === 'cash' && paymentDateOnly.getTime() === today.getTime()) {
            revenueStats.todayCash += payment.amount;
          }

          // Bugün IBAN
          if (payment.type === 'iban' && paymentDateOnly.getTime() === today.getTime()) {
            revenueStats.todayIban += payment.amount;
          }

          // Dün Kredi Kartı (kredi kartı ödemeleri bir gün gecikmeyle yansır)
          if (payment.type === 'credit_card' && paymentDateOnly.getTime() === yesterday.getTime()) {
            revenueStats.yesterdayCreditCard += payment.amount;
          }
        });

        // Sipariş tarihi yoksa zaman bazlı istatistiklere dahil etme
        if (!customer.orderDate) return;

        // Müşterinin sipariş tarihini al
        let customerDate;
        if (customer.orderDate?.toDate) {
          customerDate = customer.orderDate.toDate();
        } else {
          customerDate = new Date(customer.orderDate);
        }

        // Bugün - Bugün sipariş veren müşterilerin ödenen tutarları
        const customerDateOnly = new Date(customerDate.getFullYear(), customerDate.getMonth(), customerDate.getDate());
        if (customerDateOnly.getTime() === today.getTime()) {
          revenueStats.today += paidAmount;
        }

        // Bu hafta - Son 7 gün içinde sipariş veren müşterilerin ödenen tutarları (bugün dahil)
        const sevenDaysAgoRevenue = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        sevenDaysAgoRevenue.setHours(0, 0, 0, 0);
        if (customerDate >= sevenDaysAgoRevenue) {
          revenueStats.thisWeek += paidAmount;
        }

        // Bu ay - Bu ay sipariş veren müşterilerin ödenen tutarları
        if (customerDate >= thisMonth) {
          revenueStats.thisMonth += paidAmount;
        }

        // Bu yıl - Bu yıl sipariş veren müşterilerin ödenen tutarları
        if (customerDate >= thisYear) {
          revenueStats.thisYear += paidAmount;
        }

        // Zaman Bazlı Kâr Hesaplama (Sadece onaylanmış/yeni sipariş olmayanlar için)
        if (hasValidCostOrderAll && validStatusesForProfit.includes(customer.orderStatus)) {
          const profitFromCustomer = paidAmount - totalCostForCustomerAll;

          if (customerDateOnly.getTime() === today.getTime()) {
            revenueStats.todayProfit += profitFromCustomer;
          }
          if (customerDate >= sevenDaysAgoRevenue) {
            revenueStats.thisWeekProfit += profitFromCustomer;
          }
          if (customerDate >= thisMonth) {
            revenueStats.thisMonthProfit += profitFromCustomer;
          }
          if (customerDate >= thisYear) {
            revenueStats.thisYearProfit += profitFromCustomer;
          }
        }

        // Geçen yıl karşılaştırmaları
        if (customerDate >= lastYearYearStart && customerDate <= lastYearYearEnd) {
          lastYearStatsTemp.total += 1;
        }

        if (customerDate >= lastYearTodayStart && customerDate <= lastYearTodayEnd) {
          lastYearStatsTemp.today += 1;
        }

        if (customerDate >= lastYearWeekStart && customerDate <= lastYearWeekEnd) {
          lastYearStatsTemp.thisWeek += 1;
        }

        if (customerDate >= lastYearMonthStart && customerDate <= lastYearMonthEnd) {
          lastYearStatsTemp.thisMonth += 1;
        }
      });

      // Aylık sipariş trendi (mevcut yıl)
      const monthLabels = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
      const currentYear = now.getFullYear();
      const monthlyTrendCounts = Array(12).fill(0);
      const monthlyCustomerCounts = Array(12).fill(0);

      // Sipariş tarihlerini müşteriden okur (orders içinde orderDate yok)
      const customerOrderDateMap = new Map();
      customers.forEach(customer => {
        const dateToUse = customer.orderDate;
        let parsed = null;
        if (dateToUse?.toDate) {
          parsed = dateToUse.toDate();
        } else if (dateToUse) {
          parsed = new Date(dateToUse);
        }
        if (parsed && !isNaN(parsed.getTime())) {
          customerOrderDateMap.set(customer.id, parsed);
        }
      });

      orders.forEach(order => {
        // Sipariş Tarihi müşteri kaydından alınır; yoksa ekleme
        const orderDate = customerOrderDateMap.get(order.customerId);
        if (!orderDate) return;
        if (orderDate.getFullYear() === currentYear) {
          let orderQuantity = 0;

          if (order.productType === 'plise') {
            // Plise siparişleri için pliseItems listesindeki adetleri topla
            if (Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
              orderQuantity = order.pliseItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0);
            } else {
              // Yedek olarak ana quantity alanını kontrol et
              orderQuantity = parseInt(order.quantity) || 1;
            }
          } else {
            // Diğer tüm ürünler için ana quantity alanını kullan
            orderQuantity = parseInt(order.quantity) || 1;
          }

          monthlyTrendCounts[orderDate.getMonth()] += orderQuantity;
        }
      });

      customers.forEach(customer => {
        // Müşteri trendi de sipariş tarihine göre; tarih yoksa ekleme
        const dateToUse = customer.orderDate;
        let customerDate = null;

        if (dateToUse?.toDate) {
          customerDate = dateToUse.toDate();
        } else if (dateToUse) {
          customerDate = new Date(dateToUse);
        }

        if (!customerDate || isNaN(customerDate.getTime())) return;

        if (customerDate.getFullYear() === currentYear) {
          monthlyCustomerCounts[customerDate.getMonth()] += 1;
        }
      });

      const monthlyTrendData = monthLabels.map((label, index) => ({
        month: label,
        count: monthlyTrendCounts[index]
      }));

      const monthlyCustomerTrendData = monthLabels.map((label, index) => ({
        month: label,
        count: monthlyCustomerCounts[index]
      }));

      // Aylık Kazanç ve Net Kazanç Trendi Hesaplama
      const monthlyEarningsCounts = Array(12).fill(0);
      const monthlyNetProfitCounts = Array(12).fill(0);

      customers.forEach(customer => {
        // Müşterinin sipariş tarihini al
        const dateToUse = customer.orderDate;
        let customerDate = null;

        if (dateToUse?.toDate) {
          customerDate = dateToUse.toDate();
        } else if (dateToUse) {
          customerDate = new Date(dateToUse);
        }

        if (!customerDate || isNaN(customerDate.getTime())) return;
        if (customerDate.getFullYear() !== currentYear) return;

        // Müşterinin ödenen tutarını al (gerçek kazanç)
        const paidAmount = parseFloat(customer.paidAmount || 0);

        // Aylık kazanca ekle (ödenen tutar)
        monthlyEarningsCounts[customerDate.getMonth()] += paidAmount;

        // Sipariş maliyetleri üzerinden Net Kazanç Hesapla
        const customerOrders = orders.filter(o => o.customerId === customer.id);
        let totalCostForCustomer = 0;
        let hasValidCostOrder = false;

        customerOrders.forEach(order => {
          const costVal = computeDashboardOrderCost(order);
          totalCostForCustomer += costVal;
          if (costVal > 0 || order.totalCost !== undefined || order.unitCost || order.baseUnitCost || order.fabricCost) {
            hasValidCostOrder = true;
          }
        });

        // Sadece geçerli (maliyeti olan yani yeni sistem) ve durumu onaylanmış (Yeni Sipariş olmayan) siparişleri kar hesaplamasına dahil et
        const validStatusesForProfitGraph = ['atolye', 'firma', 'montaj', 'delivered'];
        if ((hasValidCostOrder || customerOrders.length > 0) && validStatusesForProfitGraph.includes(customer.orderStatus)) {
          const netProfit = paidAmount - totalCostForCustomer;
          monthlyNetProfitCounts[customerDate.getMonth()] += netProfit;
        }
      });

      const monthlyEarningsTrendData = monthLabels.map((label, index) => ({
        month: label,
        earnings: monthlyEarningsCounts[index]
      }));
      const monthlyNetProfitTrendData = monthLabels.map((label, index) => ({
        month: label,
        netProfit: monthlyNetProfitCounts[index]
      }));

      // Teslimat Hatırlatıcı — Perakende
      const retailDeliveryEntries = customers
        .filter(c => c.orderStatus && c.orderStatus !== 'delivered' && c.orderStatus !== 'cancelled' && c.deliveryDate)
        .map(customer => ({
          id: customer.id,
          type: 'retail',
          name: customer.name,
          phone: customer.phone1,
          address: customer.address,
          deliveryDate: customer.deliveryDate,
          orderStatus: customer.orderStatus
        }));
      const deliveryData = buildDeliveryReminders(retailDeliveryEntries);

      // ==========================================
      // BAYİ İSTATİSTİKLERİ (DEALER SESSIONS)
      // ==========================================
      // Sistemde hala aktif (silinmemiş) olan bayileri çek
      const dealersSnapshot = await getDocs(collection(db, 'dealers'));
      let activeDealers = dealersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const activeDealerIds = activeDealers.map(d => d.id);

      // Bayi Sipariş Listelerini çek
      const dealerSessionsSnapshot = await getDocs(collection(db, 'dealer_sessions'));
      let dealerSessions = dealerSessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // SADECE silinmemiş, aktif bayilere ait olan Sipariş Listelerini (Sessions) tut
      dealerSessions = dealerSessions.filter(s => activeDealerIds.includes(s.dealerId));

      if (isYazilimci && user?.uid) {
        dealerSessions = dealerSessions.filter(s => s.createdBy === user.uid);
      }

      // Teslimat Hatırlatıcı — Bayi Sipariş Listeleri
      const dealerById = Object.fromEntries(activeDealers.map(d => [d.id, d]));
      const dealerDeliveryEntries = dealerSessions
        .filter(s => s.deliveryDate && s.orderStatus !== 'delivered' && s.orderStatus !== 'cancelled')
        .map(session => {
          const dealer = dealerById[session.dealerId] || {};
          return {
            id: session.id,
            type: 'dealer',
            name: session.name || `${dealer.companyName || 'Bayi'} Sipariş Listesi`,
            dealerName: dealer.companyName || '',
            phone: dealer.phone1 || '',
            address: dealer.address || '',
            deliveryDate: session.deliveryDate,
            orderStatus: session.orderStatus
          };
        });
      const dealerDeliveryData = buildDeliveryReminders(dealerDeliveryEntries);

      // Tedarikçi Alım Listelerini çek
      const supplierSessionsSnapshot = await getDocs(collection(db, 'supplier_sessions'));
      let supplierSessions = supplierSessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // SADECE silinmemiş, aktif tedarikçilere ait olan Alım Listelerini (Sessions) tut
      supplierSessions = supplierSessions.filter(s => activeDealerIds.includes(s.dealerId));

      if (isYazilimci && user?.uid) {
        supplierSessions = supplierSessions.filter(s => s.createdBy === user.uid);
      }

      const dealerSessionIds = dealerSessions.map(s => s.id);
      const dealerOrders = orders.filter(o => dealerSessionIds.includes(o.customerId));

      const totalDealerProducts = dealerOrders.reduce((sum, order) => {
        if (order.productType === 'plise' && Array.isArray(order.pliseItems)) {
          return sum + order.pliseItems.reduce((pSum, item) => pSum + (parseInt(item.quantity) || 0), 0);
        }
        return sum + (parseInt(order.quantity) || 0);
      }, 0);

      const dealerStatsData = {
        total: dealerSessions.length,
        totalProducts: totalDealerProducts,
        today: 0,
        thisWeek: 0,
        thisMonth: 0,
        thisYear: 0
      };

      let supplierTotalDebtTemp = 0;
      let supplierTotalPurchasesTemp = 0;
      let supplierTotalPaidTemp = 0;

      // Anlık exchange rates (ref üzerinden erişim — stale closure'dan güvenli)
      const currentExchangeRates = supplierExchangeRatesRef.current;

      supplierSessions.forEach(session => {
        if (session.orderStatus === 'cancelled') return;

        const cur = session.currency || 'TRY';
        const rate = (cur === 'TRY') ? 1 : (parseFloat(currentExchangeRates[cur]) || 1);

        const totalAmt = parseFloat(session.totalOrderAmount || 0);
        const paid = parseFloat(session.paidAmount || 0);
        const remaining = getSupplierSessionRemaining(session);

        // Dashboard toplamları TL bazında
        supplierTotalPurchasesTemp += totalAmt * rate;
        supplierTotalPaidTemp += paid * rate;
        supplierTotalDebtTemp += remaining * rate;
      });

      // Tedarikçilere Yapılan Harici / Manuel Ödemeleri (supplierDirectPayments) Tedarikçi İstatistiklerine Dahil Et
      activeDealers.forEach((dealer) => {
        const supplierDirectPayments = dealer.supplierDirectPayments || [];
        if (!Array.isArray(supplierDirectPayments) || supplierDirectPayments.length === 0) return;

        supplierDirectPayments.forEach((payment) => {
          const amt = parseFloat(payment.amount || 0);
          if (!amt || amt <= 0) return;

          const cur = payment.currency || 'TRY';
          const rate = (cur === 'TRY') ? 1 : (parseFloat(currentExchangeRates[cur]) || 1);
          const amtTry = amt * rate;

          supplierTotalPaidTemp += amtTry;
          supplierTotalDebtTemp -= amtTry;
        });
      });

      const dealerRevStatsData = {
        totalAmount: 0,
        paidAmount: 0,
        totalNetProfit: 0,
        remainingAmount: 0,
        today: 0,
        thisWeek: 0,
        thisMonth: 0,
        thisYear: 0,
        potentialNetProfit: 0,
        todayProfit: 0,
        thisWeekProfit: 0,
        thisMonthProfit: 0,
        thisYearProfit: 0,
        supplierTotalDebt: supplierTotalDebtTemp,
        supplierTotalPurchases: supplierTotalPurchasesTemp,
        supplierTotalPaid: supplierTotalPaidTemp
      };

      const dealerMonthlyTrendCounts = Array(12).fill(0);
      const dealerMonthlyEarningsCounts = Array(12).fill(0);
      const dealerMonthlyNetProfitCounts = Array(12).fill(0);

      // Ana Sipariş Listeleri (Sessions) üzerinden istatistik ve gelir hesaplamaları
      dealerSessions.forEach(session => {
        let sessionDate = null;
        if (session.orderDate) {
          sessionDate = new Date(session.orderDate + 'T00:00:00');
        } else if (session.sessionDate) {
          sessionDate = new Date(session.sessionDate + 'T00:00:00');
        } else if (session.createdAt?.toDate) {
          sessionDate = session.createdAt.toDate();
        } else if (session.createdAt) {
          sessionDate = new Date(session.createdAt);
        }

        // Toplam tutarı siparişlerden canlı hesapla (indirim/vade/iskonto dahil)
        const sessionOrdersForTotal = dealerOrders.filter(o => o.customerId === session.id);
        const totalAmt = (session.manualTotalOverride != null && session.manualTotalOverride !== '')
          ? parseFloat(session.manualTotalOverride)
          : sessionOrdersForTotal.length > 0
            ? calculateCustomerGrandTotal(
                { vadeliYuzde: session.vadeliYuzde, genelIskontoYuzde: session.genelIskontoYuzde },
                sessionOrdersForTotal
              )
            : parseFloat(session.totalOrderAmount || 0);
        const paidAmt = parseFloat(session.paidAmount || 0);
        const remAmt = parseFloat(session.remainingAmount ?? (totalAmt - paidAmt));

        dealerRevStatsData.totalAmount += totalAmt;
        dealerRevStatsData.paidAmount += paidAmt;

        // Sipariş maliyetleri üzerinden Toplam Net Kazanç Hesapla
        const sessionOrdersAll = dealerOrders.filter(o => o.customerId === session.id);
        let totalCostForSessionAll = 0;
        let hasValidCostOrderAll = false;

        sessionOrdersAll.forEach(order => {
          const costValue = computeDashboardOrderCost(order);
          totalCostForSessionAll += costValue;
          if (costValue > 0 || order.totalCost !== undefined || order.unitCost || order.baseUnitCost || order.fabricCost) {
            hasValidCostOrderAll = true;
          }
        });

        if (hasValidCostOrderAll || sessionOrdersAll.length > 0) {
          // Potansiyel Net Kazanç: Toplam Beklenen Tutar - Toplam Maliyet (iptal hariç)
          if (session.orderStatus !== 'cancelled') {
            dealerRevStatsData.potentialNetProfit += (totalAmt - totalCostForSessionAll);
          }

          // Gerçek Net Kazanç: Alınan Ödeme - Toplam Maliyet (iptal hariç)
          if (session.orderStatus !== 'cancelled') {
            dealerRevStatsData.totalNetProfit += (paidAmt - totalCostForSessionAll);
          }
        }

        if (session.orderStatus !== 'cancelled') {
          dealerRevStatsData.remainingAmount += remAmt;
        }

        if (!sessionDate || isNaN(sessionDate.getTime())) return;

        const dateOnly = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());

        // Stats
        if (dateOnly.getTime() === todayDelivery.getTime()) {
          dealerStatsData.today += 1;
          dealerRevStatsData.today += paidAmt;
        }

        const sevenDaysAgoDealer = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        sevenDaysAgoDealer.setHours(0, 0, 0, 0);
        if (sessionDate >= sevenDaysAgoDealer) {
          dealerStatsData.thisWeek += 1;
          dealerRevStatsData.thisWeek += paidAmt;
        }

        if (sessionDate >= thisMonth) {
          dealerStatsData.thisMonth += 1;
          dealerRevStatsData.thisMonth += paidAmt;
        }

        if (sessionDate >= thisYearDealer) {
          dealerStatsData.thisYear += 1;
          dealerRevStatsData.thisYear += paidAmt;
        }

        // Zaman Bazlı Kâr Hesaplama (iptal edilmemiş olanlar)
        if ((hasValidCostOrderAll || sessionOrdersAll.length > 0) && session.orderStatus !== 'cancelled') {
          const profitFromSession = paidAmt - totalCostForSessionAll;

          if (dateOnly.getTime() === todayDelivery.getTime()) {
            dealerRevStatsData.todayProfit += profitFromSession;
          }
          if (sessionDate >= sevenDaysAgoDealer) {
            dealerRevStatsData.thisWeekProfit += profitFromSession;
          }
          if (sessionDate >= thisMonth) {
            dealerRevStatsData.thisMonthProfit += profitFromSession;
          }
          if (sessionDate >= thisYearDealer) {
            dealerRevStatsData.thisYearProfit += profitFromSession;
          }
        }

        if (sessionDate.getFullYear() === currentYear) {
          dealerMonthlyTrendCounts[sessionDate.getMonth()] += 1;
          dealerMonthlyEarningsCounts[sessionDate.getMonth()] += paidAmt;

          // Sipariş maliyetleri üzerinden Net Kazanç Hesapla
          const sessionOrders = dealerOrders.filter(o => o.customerId === session.id);
          let totalCostForSession = 0;

          sessionOrders.forEach(order => {
            totalCostForSession += computeDashboardOrderCost(order);
          });

          if (session.orderStatus !== 'cancelled') {
            dealerMonthlyNetProfitCounts[sessionDate.getMonth()] += (paidAmt - totalCostForSession);
          }
        }
      });

      // Harici / Manuel Alınan Ödemeleri (directPayments) Anasayfa İstatistiklerine Dahil Et
      activeDealers.forEach((dealer) => {
        const directPayments = dealer.directPayments || dealer.paymentHistory || [];
        if (!Array.isArray(directPayments) || directPayments.length === 0) return;

        directPayments.forEach((payment) => {
          const amt = parseFloat(payment.amount || 0);
          if (!amt || amt <= 0) return;

          // 1) Toplam Tahsil Edilen Tutara ve Net/Potansiyel Kâra Ekle (Maliyet 0 kabul edilir)
          dealerRevStatsData.paidAmount += amt;
          dealerRevStatsData.totalNetProfit += amt;
          dealerRevStatsData.potentialNetProfit += amt;

          // 2) Açık Bakiyeyi Alınan Tahsilat Kadar Düşür
          dealerRevStatsData.remainingAmount = Math.max(0, dealerRevStatsData.remainingAmount - amt);

          // Ödeme Tarihini Ayrıştır
          let pDate = null;
          if (payment.date) {
            pDate = new Date(payment.date + 'T00:00:00');
          } else if (payment.createdAt) {
            pDate = new Date(payment.createdAt);
          }

          if (!pDate || isNaN(pDate.getTime())) return;

          const pDateOnly = new Date(pDate.getFullYear(), pDate.getMonth(), pDate.getDate());

          // Bugün
          if (pDateOnly.getTime() === todayDelivery.getTime()) {
            dealerRevStatsData.today += amt;
            dealerRevStatsData.todayProfit += amt;
          }

          // Bu Hafta (son 7 gün)
          const sevenDaysAgoDealer = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
          sevenDaysAgoDealer.setHours(0, 0, 0, 0);
          if (pDate >= sevenDaysAgoDealer) {
            dealerRevStatsData.thisWeek += amt;
            dealerRevStatsData.thisWeekProfit += amt;
          }

          // Bu Ay
          if (pDate >= thisMonth) {
            dealerRevStatsData.thisMonth += amt;
            dealerRevStatsData.thisMonthProfit += amt;
          }

          // Bu Yıl
          if (pDate >= thisYearDealer) {
            dealerRevStatsData.thisYear += amt;
            dealerRevStatsData.thisYearProfit += amt;
          }

          // Aylık Gelir ve Net Kâr Grafiği
          if (pDate.getFullYear() === currentYear) {
            dealerMonthlyEarningsCounts[pDate.getMonth()] += amt;
            dealerMonthlyNetProfitCounts[pDate.getMonth()] += amt;
          }
        });
      });

      const dealerMonthlyTrendData = monthLabels.map((label, index) => ({
        month: label,
        count: dealerMonthlyTrendCounts[index]
      }));
      const dealerMonthlyEarningsData = monthLabels.map((label, index) => ({
        month: label,
        earnings: dealerMonthlyEarningsCounts[index]
      }));
      const dealerMonthlyNetProfitData = monthLabels.map((label, index) => ({
        month: label,
        netProfit: dealerMonthlyNetProfitCounts[index]
      }));

      // Gerçek sipariş kalemleri (orders) için aylık trend — bağlı session'ın orderDate'ine göre
      // Session ID → orderDate lookup (order kalemleri için doğru ay tespiti)
      const sessionDateMap = {};
      dealerSessions.forEach(s => {
        if (s.orderDate) {
          sessionDateMap[s.id] = new Date(s.orderDate + 'T00:00:00');
        } else if (s.sessionDate) {
          sessionDateMap[s.id] = new Date(s.sessionDate + 'T00:00:00');
        } else if (s.createdAt?.toDate) {
          sessionDateMap[s.id] = s.createdAt.toDate();
        } else if (s.createdAt) {
          sessionDateMap[s.id] = new Date(s.createdAt);
        }
      });

      const dealerOrderItemMonthlyCounts = Array(12).fill(0);
      dealerOrders.forEach(order => {
        // Sipariş kaleminin tarihi olarak bağlı session'ın orderDate'ini kullan
        let orderDate = sessionDateMap[order.customerId] || null;

        // Fallback: session bulunamazsa order'ın kendi createdAt'ini kullan
        if (!orderDate) {
          if (order.createdAt?.toDate) {
            orderDate = order.createdAt.toDate();
          } else if (order.createdAt) {
            orderDate = new Date(order.createdAt);
          }
        }
        if (!orderDate || isNaN(orderDate.getTime())) return;

        if (orderDate.getFullYear() === currentYear) {
          let orderQuantity = 0;

          if (order.productType === 'plise') {
            // Plise siparişleri için pliseItems listesindeki adetleri topla
            if (Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
              orderQuantity = order.pliseItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0);
            } else {
              // Yedek olarak ana quantity alanını kontrol et
              orderQuantity = parseInt(order.quantity) || 1;
            }
          } else {
            // Diğer tüm ürünler için ana quantity alanını kullan
            orderQuantity = parseInt(order.quantity) || 1;
          }

          dealerOrderItemMonthlyCounts[orderDate.getMonth()] += orderQuantity;
        }
      });
      const dealerMonthlyOrderItemData = monthLabels.map((label, index) => ({
        month: label,
        count: dealerOrderItemMonthlyCounts[index]
      }));

      // Set Retail Stats
      setCustomerStats(customerStats);
      setRevenueStats(revenueStats);
      setLastYearStats(lastYearStatsTemp);
      setMonthlyOrderTrend(monthlyTrendData);
      setMonthlyCustomerTrend(monthlyCustomerTrendData);
      setMonthlyEarningsTrend(monthlyEarningsTrendData);
      setMonthlyNetProfitTrend(monthlyNetProfitTrendData);
      setDeliveryReminders(deliveryData);
      setDealerDeliveryReminders(dealerDeliveryData);

      // Set Dealer Stats
      setDealerStats(dealerStatsData);
      setDealerRevenueStats(dealerRevStatsData);
      setDealerMonthlyOrderTrend(dealerMonthlyTrendData);
      setDealerMonthlyOrderItemTrend(dealerMonthlyOrderItemData);
      setDealerMonthlyEarningsTrend(dealerMonthlyEarningsData);
      setDealerMonthlyNetProfitTrend(dealerMonthlyNetProfitData);

      // Cache'e ve Materialized View'e yaz
      const dataToCache = {
        customerStats,
        revenueStats,
        lastYearStatsTemp,
        monthlyTrendData,
        monthlyCustomerTrendData,
        monthlyEarningsTrendData,
        monthlyNetProfitTrendData: monthlyNetProfitTrendData,
        deliveryData,
        dealerDeliveryData,
        dealerStatsData,
        dealerRevStatsData,
        dealerMonthlyTrendData,
        dealerMonthlyOrderItemData,
        dealerMonthlyEarningsData,
        dealerMonthlyNetProfitData
      };

      const updateTime = new Date();
      localStorage.setItem('dashboard_stats_cache', JSON.stringify(dataToCache));
      localStorage.setItem('dashboard_stats_time', updateTime.getTime().toString());
      setStatsLastUpdated(updateTime);

      // Yazılımcı filtresi yoksa bu güncel veriyi tek bir write ile stats/dashboard belgesine de yaz
      if (!isFilteredView) {
        try {
          const statsDocRef = doc(db, 'stats', 'dashboard');
          await setDoc(statsDocRef, {
            ...dataToCache,
            updatedAt: updateTime.getTime()
          });
        } catch (writeErr) {
          console.warn('Birleştirilmiş istatistik belgesi güncellenemedi (Firestore izin kısıtlaması, yerel önbellek kullanılıyor):', writeErr?.message || writeErr);
        }
      }

    } catch (error) {
      console.error('İstatistikler yüklenirken hata:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, [isYazilimci, user?.uid]);

  const handleCustomerChange = useCallback(() => {
    localStorage.removeItem('dashboard_stats_cache');
    localStorage.removeItem('dashboard_stats_time');
    setStatsLastUpdated(null);
    fetchAllStats(true); // Canlı verileri tekrar hesapla
  }, [fetchAllStats]);

  // İstatistikleri yükle
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchAllStats();
    }
  }, [fetchAllStats, activeTab]);

  // Aktif sekme değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);



  // Firma bilgilerini Firebase'den çek
  const fetchCompanies = useCallback(async () => {
    try {
      setIsLoadingCompanies(true);
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      const companiesData = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // İsme göre sırala
      companiesData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCompanies(companiesData);
    } catch (error) {
      console.error('Firma bilgileri yüklenirken hata:', error);
      setCompanyError('Firma bilgileri yüklenirken bir hata oluştu.');
    } finally {
      setIsLoadingCompanies(false);
    }
  }, []);

  // Firma bilgileri modalı açıldığında verileri çek
  useEffect(() => {
    if (showCompanyInfo) {
      fetchCompanies();
    }
  }, [showCompanyInfo, fetchCompanies]);

  // Firma ekle veya güncelle
  const handleSaveCompany = async (e) => {
    e.preventDefault();
    setCompanyError(null);
    setCompanySuccess(false);

    // Validasyonlar
    if (!companyForm.name || !companyForm.phone) {
      setCompanyError('Lütfen firma adı ve telefon numarasını girin.');
      return;
    }

    // Telefon numarası validasyonu (sadece rakam ve + işareti)
    const phoneRegex = /^[+]?[0-9]{10,15}$/;
    if (!phoneRegex.test(companyForm.phone.replace(/\s/g, ''))) {
      setCompanyError('Geçerli bir telefon numarası girin (örn: 905321234567 veya +905321234567)');
      return;
    }

    try {
      const phoneNumber = companyForm.phone.replace(/\s/g, ''); // Boşlukları temizle

      if (editingCompany) {
        // Güncelleme
        await updateDoc(doc(db, 'companies', editingCompany.id), {
          name: companyForm.name.trim(),
          phone: phoneNumber,
          updatedAt: new Date()
        });
        setCompanySuccess('Firma bilgileri başarıyla güncellendi!');
      } else {
        // Yeni ekleme
        // Aynı isimde firma var mı kontrol et
        const existingCompany = companies.find(c =>
          c.name.toLowerCase().trim() === companyForm.name.toLowerCase().trim()
        );
        if (existingCompany) {
          setCompanyError('Bu isimde bir firma zaten mevcut.');
          return;
        }

        await addDoc(collection(db, 'companies'), {
          name: companyForm.name.trim(),
          phone: phoneNumber,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: user?.uid || null
        });
        setCompanySuccess('Firma başarıyla eklendi!');
      }

      // Formu temizle
      setCompanyForm({ name: '', phone: '' });
      setEditingCompany(null);

      // Listeyi yeniden yükle
      await fetchCompanies();

      // 3 saniye sonra başarı mesajını kaldır
      setTimeout(() => {
        setCompanySuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Firma kaydedilirken hata:', error);
      let errorMessage = 'Firma kaydedilirken bir hata oluştu.';
      if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
        errorMessage = 'Firma ekleme yetkiniz bulunmamaktadır. Lütfen yöneticinizle iletişime geçin.';
      } else if (error.message) {
        errorMessage = `Firma kaydedilirken bir hata oluştu: ${error.message}`;
      }
      setCompanyError(errorMessage);
    }
  };

  // Firma düzenle
  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name || '',
      phone: company.phone || ''
    });
    setCompanyError(null);
    setCompanySuccess(false);
  };

  // Firma silme modal'ını aç
  const handleDeleteCompanyClick = (company) => {
    setDeleteCompanyModal(company);
  };

  // Firma silme modal'ını kapat
  const handleCloseDeleteCompanyModal = () => {
    setDeleteCompanyModal(null);
  };

  // Firma sil
  const handleDeleteCompany = async () => {
    if (!deleteCompanyModal) return;

    try {
      await deleteDoc(doc(db, 'companies', deleteCompanyModal.id));
      setCompanySuccess('Firma başarıyla silindi!');
      setDeleteCompanyModal(null);
      await fetchCompanies();

      // 3 saniye sonra başarı mesajını kaldır
      setTimeout(() => {
        setCompanySuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Firma silinirken hata:', error);
      setCompanyError('Firma silinirken bir hata oluştu: ' + error.message);
      setDeleteCompanyModal(null);
    }
  };

  // Firma formunu iptal et
  const handleCancelCompany = () => {
    setCompanyForm({ name: '', phone: '' });
    setEditingCompany(null);
    setCompanyError(null);
    setCompanySuccess(false);
  };

  // ======== KULLANICI YÖNETİMİ FONKSİYONLARI ========

  // Kullanıcıları getir
  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;

    setIsLoadingUsers(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(user => !user.disabled); // Disabled kullanıcıları filtrele
      setUsers(usersData);
    } catch (error) {
      console.error('Kullanıcılar alınırken hata:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [isAdmin]);

  // Kullanıcıları yükle (admin ise)
  useEffect(() => {
    if (isAdmin && activeTab === 'users') {
      fetchUsers();
    }
  }, [isAdmin, activeTab, fetchUsers]);

  // Yeni kullanıcı oluştur
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserFormError(null);

    if (!newUserForm.email || !newUserForm.password || !newUserForm.displayName) {
      setUserFormError('Tüm alanları doldurun');
      return;
    }

    if (newUserForm.password.length < 6) {
      setUserFormError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    // Yazılımcı rolü kısıtlamaları
    if (newUserForm.role === ROLES.YAZILIMCI) {
      if (newUserForm.email !== 'kahramantechinfo@gmail.com') {
        setUserFormError('Yazılımcı rolü yalnızca kahramantechinfo@gmail.com için oluşturulabilir');
        return;
      }
      const mevcutYazilimci = users.find(u => u.role === ROLES.YAZILIMCI);
      if (mevcutYazilimci) {
        setUserFormError('Sistemde zaten bir Yazılımcı kullanıcısı mevcut');
        return;
      }
    }

    try {
      // Firebase Auth'da kullanıcı oluştur - Secondary auth instance kullanıyoruz
      // Ana kullanıcının oturumunu kapatmadan yeni kullanıcı oluşturmak için
      const { initializeApp, getApps, deleteApp } = await import('firebase/app');
      const { getAuth, createUserWithEmailAndPassword: createUser } = await import('firebase/auth');

      // Mevcut Secondary app varsa sil
      const apps = getApps();
      const existingSecondary = apps.find(app => app.name === 'Secondary');
      if (existingSecondary) {
        await deleteApp(existingSecondary);
      }

      // Geçici bir Firebase app instance oluştur (constants'dan config al)
      const secondaryApp = initializeApp(FIREBASE_CONFIG, 'Secondary');
      const secondaryAuth = getAuth(secondaryApp);

      try {
        const userCredential = await createUser(secondaryAuth, newUserForm.email, newUserForm.password);
        const newUser = userCredential.user;

        // Firestore'da kullanıcı kaydı oluştur
        await setDoc(doc(db, 'users', newUser.uid), {
          email: newUserForm.email,
          displayName: newUserForm.displayName,
          role: newUserForm.role,
          createdAt: new Date(),
          createdBy: user.uid
        });

        // Secondary app'i temizle
        await deleteApp(secondaryApp);

        // Formu sıfırla ve modalı kapat
        setNewUserForm({
          email: '',
          password: '',
          displayName: '',
          role: ROLES.SATISCI
        });
        setShowAddUserModal(false);

        // Kullanıcı listesini yenile
        fetchUsers();

        showNotificationModal('success', 'Başarılı', `${newUserForm.displayName} kullanıcısı başarıyla oluşturuldu.`);
      } catch (error) {
        await deleteApp(secondaryApp);
        throw error;
      }
    } catch (error) {
      console.error('Kullanıcı oluşturulurken hata:', error);
      if (error.code === 'auth/email-already-in-use') {
        setUserFormError('Bu e-posta adresi zaten kullanılıyor');
      } else if (error.code === 'auth/invalid-email') {
        setUserFormError('Geçersiz e-posta adresi');
      } else if (error.code === 'auth/weak-password') {
        setUserFormError('Şifre çok zayıf');
      } else {
        setUserFormError('Kullanıcı oluşturulurken bir hata oluştu');
      }
    }
  };

  // Kullanıcı rolünü güncelle
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: new Date(),
        updatedBy: user.uid
      });

      // Local state güncelle
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, role: newRole } : u
      ));
      setEditingUserRole(null);

      showNotificationModal('success', 'Başarılı', 'Kullanıcı rolü güncellendi');
    } catch (error) {
      console.error('Rol güncellenirken hata:', error);
      showNotificationModal('error', 'Hata', 'Rol güncellenirken bir hata oluştu');
    }
  };

  // Kullanıcı sil - Kullanıcıyı disabled olarak işaretle (Firestore'dan silme, sadece devre dışı bırak)
  const handleDeleteUser = async (userId) => {
    try {
      // Önce kullanıcı bilgilerini al
      const userToDelete = users.find(u => u.id === userId);
      if (!userToDelete) {
        showNotificationModal('error', 'Hata', 'Kullanıcı bulunamadı');
        return;
      }

      // Firebase Auth'dan kullanıcıyı silmek için Cloud Functions endpoint'ini dene
      try {
        const functions = await import('firebase/functions');
        const { getFunctions, httpsCallable } = functions;
        const funcs = getFunctions();
        const deleteUserFunction = httpsCallable(funcs, 'deleteUser');

        // Cloud Functions ile Firebase Auth'dan da sil
        await deleteUserFunction({ userId: userId, email: userToDelete.email });

        // Başarılı olursa Firestore'dan da sil
        await deleteDoc(doc(db, 'users', userId));
      } catch (functionsError) {
        // Cloud Functions yoksa veya hata varsa, sadece disabled olarak işaretle
        // Firestore'dan SİLME, sadece disabled yap
        await updateDoc(doc(db, 'users', userId), {
          disabled: true,
          disabledAt: new Date(),
          disabledBy: user.uid
        });
      }

      // Kullanıcı listesini yeniden yükle
      await fetchUsers();

      setDeleteUserModal(null);
      showNotificationModal('success', 'Başarılı', 'Kullanıcı başarıyla devre dışı bırakıldı');
    } catch (error) {
      console.error('Kullanıcı silinirken hata:', error);
      showNotificationModal('error', 'Hata', 'Kullanıcı silinirken bir hata oluştu: ' + error.message);
    }
  };

  // Rol adını Türkçe olarak döndür
  const getRoleName = (role) => {
    const roleNames = {
      [ROLES.ADMIN]: 'Admin',
      [ROLES.SATISCI]: 'Satışcı',
      [ROLES.MONTAJCI]: 'Montajcı',
      [ROLES.YAZILIMCI]: 'Yazılımcı'
    };
    return roleNames[role] || role;
  };

  // Rol badge rengini döndür
  const getRoleBadgeClass = (role) => {
    const classes = {
      [ROLES.ADMIN]: 'role-badge-admin',
      [ROLES.SATISCI]: 'role-badge-satisci',
      [ROLES.MONTAJCI]: 'role-badge-montajci',
      [ROLES.YAZILIMCI]: 'role-badge-yazilimci'
    };
    return classes[role] || '';
  };

  // ======== KULLANICI YÖNETİMİ FONKSİYONLARI SONU ========

  const handleLogout = async () => {
    try {
      // Firebase'den çıkış yap
      await signOut(auth);
      // Firebase otomatik olarak oturumu temizler
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Çıkış hatası:', error);
      // Hata olsa bile ana sayfaya yönlendir
      navigate(ROUTES.LOGIN);
    }
  };


  // Backup fonksiyonu
  const handleExport = async () => {
    try {
      setIsExporting(true);
      const result = await exportData(userRole, user?.uid);
      showNotificationModal(
        'success',
        'Yedekleme Başarılı',
        'Verileriniz başarıyla yedeklendi.',
        {
          customers: result.customers,
          orders: result.orders
        }
      );
    } catch (error) {
      showNotificationModal('error', 'Yedekleme Hatası', error.message);
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Import modal'ını aç
  const handleImportClick = () => {
    setShowImportModal(true);
    setImportFile(null);
  };

  // Dosya seçimi
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        validateBackupFile(file);
        setImportFile(file);
      } catch (error) {
        showNotificationModal('error', 'Dosya Hatası', error.message);
        event.target.value = '';
      }
    }
  };

  // Import işlemi
  const handleImport = async () => {
    if (!importFile) {
      showNotificationModal('warning', 'Dosya Seçilmedi', 'Lütfen bir yedek dosyası seçin.');
      return;
    }

    try {
      setIsImporting(true);
      const result = await importData(importFile);

      // Modal'ı kapat
      setShowImportModal(false);
      setImportFile(null);

      // İstatistikleri yeniden yükle
      fetchAllStats();

      // Başarı modal'ını göster
      showNotificationModal(
        'success',
        'Yükleme Başarılı',
        'Yedek dosyası başarıyla yüklendi.',
        {
          backupCustomers: result.totalCustomers,
          backupOrders: result.totalOrders,
          addedCustomers: result.addedCustomers,
          updatedCustomers: result.updatedCustomers,
          addedOrders: result.addedOrders,
          updatedOrders: result.updatedOrders
        }
      );
    } catch (error) {
      showNotificationModal('error', 'Yükleme Hatası', error.message);
      console.error('Import error:', error);
    } finally {
      setIsImporting(false);
    }
  };

  // Modal'ı kapat
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
  };

  //Şifre değiştirme form kontrolü (Onay modalını açar)
  const handlePasswordFormSubmit = (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validasyonlar
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Lütfen tüm alanları doldurun.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Yeni şifre ve şifre onayı eşleşmiyor.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Yeni şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('Yeni şifre eski şifre ile aynı olamaz.');
      return;
    }

    setShowPasswordConfirmModal(true);
  };

  // Asıl şifre değiştirme işlemi (Onaylandıktan sonra çalışır)
  const handleChangePasswordConfirm = async () => {
    setShowPasswordConfirmModal(false);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      setIsChangingPassword(true);

      //Mevcut şifreyi doğrula
      const user = auth.currentUser;
      if (!user || !user.email) {
        setPasswordError("Kullanıcı bilgisi bulunamadı.");
        setIsChangingPassword(false);
        return;
      }

      // Mevcut şifreyi doğrula (ayrı try-catch ile)
      try {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
      } catch (authError) {
        console.error('Şifre doğrulama hatası:', authError);

        // Mevcut şifre ile ilgili hatalar
        if (authError.code === 'auth/wrong-password' ||
          authError.code === 'auth/invalid-credential' ||
          authError.code === 'auth/invalid-email' ||
          authError.message?.includes('password') ||
          authError.message?.includes('credential')) {
          setPasswordError('Mevcut şifre hatalı. Lütfen doğru şifreyi girin.');
        } else if (authError.code === 'auth/user-mismatch') {
          setPasswordError('Kullanıcı bilgisi eşleşmiyor. Lütfen tekrar giriş yapın.');
        } else if (authError.code === 'auth/requires-recent-login') {
          setPasswordError('Güvenlik nedeniyle lütfen tekrar giriş yapın ve şifre değiştirmeyi tekrar deneyin.');
        } else {
          setPasswordError('Mevcut şifre doğrulanamadı. Lütfen şifrenizi kontrol edin.');
        }
        setIsChangingPassword(false);
        return;
      }

      // Şifreyi güncelle (ayrı try-catch ile)
      try {
        await updatePassword(user, newPassword);
      } catch (updateError) {
        console.error('Şifre güncelleme hatası:', updateError);

        if (updateError.code === 'auth/weak-password') {
          setPasswordError('Yeni şifre çok zayıf. Daha güçlü bir şifre seçin (en az 6 karakter, büyük/küçük harf, rakam önerilir).');
        } else if (updateError.code === 'auth/requires-recent-login') {
          setPasswordError('Güvenlik nedeniyle lütfen tekrar giriş yapın ve şifre değiştirmeyi tekrar deneyin.');
        } else {
          setPasswordError('Şifre güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
        }
        setIsChangingPassword(false);
        return;
      }

      // Başarılı
      setPasswordSuccess('Şifreniz başarıyla değiştirildi!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // 3 saniye sonra formu kapat
      setTimeout(() => {
        setShowPasswordChange(false);
        setPasswordSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Şifre değiştirme hatası:', error);
      setPasswordError('Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Kullanıcı Yönetimi Render
  const renderUserManagement = () => {
    return (
      <div className="user-management-content">
        <div className="section-header">
          <h2><i className="fas fa-users-cog"></i> Kullanıcı Yönetimi</h2>
          <button
            className="btn-add-user"
            onClick={() => setShowAddUserModal(true)}
          >
            <i className="fas fa-user-plus"></i>
            Yeni Kullanıcı
          </button>
        </div>

        {isLoadingUsers ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Kullanıcılar yükleniyor...</span>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Kullanıcı Adı</th>
                  <th>E-posta</th>
                  <th>Rol</th>
                  <th>Oluşturulma Tarihi</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.displayName || '-'}</td>
                    <td>{u.email}</td>
                    <td>
                      {editingUserRole === u.id ? (
                        <select
                          value={u.role}
                          onChange={(e) => handleUpdateUserRole(u.id, e.target.value)}
                          onBlur={() => setEditingUserRole(null)}
                          autoFocus
                        >
                          <option value={ROLES.ADMIN}>Admin</option>
                          <option value={ROLES.SATISCI}>Satışcı</option>
                          <option value={ROLES.MONTAJCI}>Montajcı</option>
                          <option value={ROLES.YAZILIMCI}>Yazılımcı</option>
                        </select>
                      ) : (
                        <span
                          className={`role-badge ${getRoleBadgeClass(u.role)}`}
                          onClick={() => u.id !== user?.uid && setEditingUserRole(u.id)}
                          style={{ cursor: u.id !== user?.uid ? 'pointer' : 'default' }}
                          title={u.id !== user?.uid ? 'Rolü değiştirmek için tıklayın' : 'Kendi rolünüzü değiştiremezsiniz'}
                        >
                          {getRoleName(u.role)}
                        </span>
                      )}
                    </td>
                    <td>
                      {u.createdAt?.toDate?.()
                        ? new Date(u.createdAt.toDate()).toLocaleDateString('tr-TR')
                        : '-'}
                    </td>
                    <td>
                      {u.id !== user?.uid ? (
                        <button
                          className="btn-delete-user"
                          onClick={() => setDeleteUserModal(u)}
                          title="Kullanıcıyı Sil"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      ) : (
                        <span className="current-user-badge">Siz</span>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="5" className="empty-state">
                      Henüz kullanıcı bulunmamaktadır.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="role-info-section">
          <h3><i className="fas fa-info-circle"></i> Rol Yetkileri</h3>
          <div className="role-info-grid">
            <div className="role-info-card">
              <span className="role-badge role-badge-admin">Admin</span>
              <p>Tüm sisteme tam erişim. Kullanıcı oluşturma ve silme yetkisi.</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-satisci">Satışcı</span>
              <p>Müşteri ve sipariş ekleme, düzenleme, silme. Dashboard'a erişim yok.</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-montajci">Montajcı</span>
              <p>Sadece montaj durumundaki siparişleri görme ve teslim edildi olarak işaretleme.</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-yazilimci">Yazılımcı</span>
              <p>Tüm özelliklere erişim, ancak sadece kendi oluşturduğu verileri görme.</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDashboardContent = () => {
    // Satışcı dashboard'a erişemez
    if (!permissions?.canAccessDashboard) {
      return (
        <div className="access-denied-card">
          <i className="fas fa-lock"></i>
          <h2>Erişim Reddedildi</h2>
          <p>Dashboard'a erişim yetkiniz bulunmamaktadır.</p>
          <p>Müşteri Listesi sekmesinden işlemlerinizi yapabilirsiniz.</p>
        </div>
      );
    }
    return (
      <div className="dashboard-content">
        <div className="welcome-card">
          <h2>Hoş Geldiniz!</h2>
          <p>Müşteri listesi yönetim paneline başarıyla giriş yaptınız.</p>
          <p>Burada müşteri listesi işlemlerinizi gerçekleştirebilirsiniz.</p>
          {user?.email && (
            <div className="user-info">
              <p><strong>Giriş Yapan:</strong> {user.email}</p>
              {userRole && (
                <p><strong>Rol:</strong> <span className={`role-badge ${getRoleBadgeClass(userRole)}`}>{getRoleName(userRole)}</span></p>
              )}
            </div>
          )}
        </div>

        <div className="cache-info-banner">
          <div className="cache-info-text">
            <i className="fas fa-history"></i>
            <span>
              İstatistikler en son <strong>{statsLastUpdated ? statsLastUpdated.toLocaleTimeString('tr-TR') : '...'}</strong> saatinde güncellendi.
            </span>
          </div>
          <button
            className="refresh-stats-btn"
            onClick={() => fetchAllStats(true)}
            disabled={isLoadingStats}
          >
            {isLoadingStats ? (
              <><i className="fas fa-spinner fa-spin"></i> Güncelleniyor...</>
            ) : (
              <><i className="fas fa-sync-alt"></i> Verileri Yenile</>
            )}
          </button>
        </div>

        {/* Teslimat Hatırlatıcısı */}
        {(hasDeliveryReminders(deliveryReminders) || hasDeliveryReminders(dealerDeliveryReminders)) && (
          <div className="delivery-reminders-section">
            <h3><i className="fas fa-bell"></i> Teslimat Hatırlatıcısı</h3>

            <div className="dashboard-stats-tabs delivery-reminders-tabs">
              <button
                type="button"
                className={`stats-tab-btn ${statsTab === 'retail' ? 'active' : ''}`}
                onClick={() => setStatsTab('retail')}
              >
                <i className="fas fa-store"></i> Perakende
              </button>
              <button
                type="button"
                className={`stats-tab-btn ${statsTab === 'dealer' ? 'active' : ''}`}
                onClick={() => setStatsTab('dealer')}
              >
                <i className="fas fa-industry"></i> Toptan
              </button>
            </div>

            <div className="delivery-reminders-block">
              {statsTab === 'retail' ? (
                hasDeliveryReminders(deliveryReminders) ? (
                  <DeliveryReminderGrid reminders={deliveryReminders} />
                ) : (
                  <p className="delivery-reminders-empty">Perakende teslimat hatırlatıcısı bulunmuyor.</p>
                )
              ) : hasDeliveryReminders(dealerDeliveryReminders) ? (
                <DeliveryReminderGrid reminders={dealerDeliveryReminders} showDealerMeta />
              ) : (
                <p className="delivery-reminders-empty">Toptan teslimat hatırlatıcısı bulunmuyor.</p>
              )}
            </div>
          </div>
        )}

        {/* Sekme Seçici - İstatistik Tipi */}
        <div className="dashboard-stats-tabs">
          <button
            className={`stats-tab-btn ${statsTab === 'retail' ? 'active' : ''}`}
            onClick={() => setStatsTab('retail')}
          >
            <i className="fas fa-store"></i> Perakende İstatistikleri
          </button>
          <button
            className={`stats-tab-btn ${statsTab === 'dealer' ? 'active' : ''}`}
            onClick={() => setStatsTab('dealer')}
          >
            <i className="fas fa-industry"></i> Bayi İstatistikleri
          </button>
        </div>

        {/* İstatistikler (Perakende veya Bayi) */}
        <div className="stats-section">
          <h3>{statsTab === 'retail' ? 'Müşteri İstatistikleri' : 'Bayi İstatistikleri'}</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <i className={statsTab === 'retail' ? "fas fa-users" : "fas fa-cubes"}></i>
              </div>
              <div className="stat-content">
                <h3>{statsTab === 'retail' ? 'Toplam Müşteri' : 'Toplam Sipariş'}</h3>
                <div className="stat-number">
                  {isLoadingStats ? <div className="stat-spinner"></div> : (statsTab === 'retail' ? customerStats.total : dealerStats.total)}
                </div>
                <small>{statsTab === 'retail' ? 'Tüm kayıtlı müşteriler' : 'Tüm eklenen sipariş listeleri'}</small>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <i className={statsTab === 'retail' ? "fas fa-user-check" : "fas fa-list-alt"}></i>
              </div>
              <div className="stat-content">
                <h3>{statsTab === 'retail' ? 'Aktif Müşteri' : 'Toplam Parça Sayısı'}</h3>
                <div className="stat-number">
                  {isLoadingStats ? <div className="stat-spinner"></div> : (statsTab === 'retail' ? customerStats.active : dealerStats.totalProducts)}
                </div>
                <small>{statsTab === 'retail' ? 'Yeni sipariş durumunda' : 'Sipariş detayındaki kalem adedi'}</small>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-calendar-day"></i>
              </div>
              <div className="stat-content">
                <h3>Bugün</h3>
                <div className="stat-number">
                  {isLoadingStats ? <div className="stat-spinner"></div> : (statsTab === 'retail' ? customerStats.today : dealerStats.today)}
                </div>
                <small>Bugün sipariş oluşturan</small>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-calendar-week"></i>
              </div>
              <div className="stat-content">
                <h3>Bu Hafta</h3>
                <div className="stat-number">
                  {isLoadingStats ? <div className="stat-spinner"></div> : (statsTab === 'retail' ? customerStats.thisWeek : dealerStats.thisWeek)}
                </div>
                <small>Son 7 günde sipariş oluşturan</small>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-calendar-alt"></i>
              </div>
              <div className="stat-content">
                <h3>Bu Ay</h3>
                <div className="stat-number">
                  {isLoadingStats ? <div className="stat-spinner"></div> : (statsTab === 'retail' ? customerStats.thisMonth : dealerStats.thisMonth)}
                </div>
                <small>Bu ay sipariş oluşturan</small>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="stat-content">
                <h3>Bu Yıl</h3>
                <div className="stat-number">
                  {isLoadingStats ? <div className="stat-spinner"></div> : (statsTab === 'retail' ? customerStats.thisYear : dealerStats.thisYear)}
                </div>
                <small>Bu yıl sipariş oluşturan</small>
              </div>
            </div>
          </div>
        </div>

        {/* Aylık Müşteri Trendi (Sadece Perakende modunda anlamlı ama bayi için de sipariş listesi trendi yansıtılır) */}
        {statsTab === 'retail' && (
          <div className="stats-section">
            <div className="section-header">
              <h3>Aylık Müşteri Trendi ({new Date().getFullYear()})</h3>
            </div>
            <div className="chart-card">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={monthlyCustomerTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                  <XAxis dataKey="month" stroke="#b04309" />
                  <YAxis allowDecimals={false} stroke="#b04309" />
                  <Tooltip formatter={(value) => [`${value}`, 'Müşteri']} />
                  <Line type="monotone" dataKey="count" name="Müşteri" stroke="#b04309" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Aylık Sipariş Listesi/Sipariş Trendi */}
        <div className="stats-section">
          <div className="section-header">
            <h3>
              {statsTab === 'retail'
                ? `Aylık Sipariş Trendi (${new Date().getFullYear()})`
                : `Aylık Bayi Sipariş Listesi Trendi (${new Date().getFullYear()})`}
            </h3>
          </div>
          <div className="chart-card">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={statsTab === 'retail' ? monthlyOrderTrend : dealerMonthlyOrderTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                <XAxis dataKey="month" stroke="#b04309" />
                <YAxis allowDecimals={false} stroke="#b04309" />
                <Tooltip formatter={(value) => [`${value}`, statsTab === 'retail' ? 'Sipariş' : 'Sipariş Listesi']} />
                <Line type="monotone" dataKey="count" name={statsTab === 'retail' ? 'Sipariş' : 'Sipariş Listesi'} stroke="#b04309" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bayi modunda ek: Aylık Sipariş Kalemi Trendi */}
        {statsTab === 'dealer' && (
          <div className="stats-section">
            <div className="section-header">
              <h3>Aylık Sipariş Trendi ({new Date().getFullYear()})</h3>
            </div>
            <div className="chart-card">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={dealerMonthlyOrderItemTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                  <XAxis dataKey="month" stroke="#7c3aed" />
                  <YAxis allowDecimals={false} stroke="#7c3aed" />
                  <Tooltip formatter={(value) => [`${value}`, 'Sipariş']} />
                  <Line
                    type="monotone"
                    dataKey="count"
                    name="Sipariş"
                    stroke="#7c3aed"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#7c3aed' }}
                    activeDot={{ r: 6, fill: '#5b21b6' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Aylık Kazanç Trendi */}
        <div className="stats-section">
          <div className="section-header">
            <h3>Aylık Kazanç Trendi ({new Date().getFullYear()})</h3>
          </div>
          <div className="chart-card">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={statsTab === 'retail' ? monthlyEarningsTrend : dealerMonthlyEarningsTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                <XAxis dataKey="month" stroke="#b04309" />
                <YAxis
                  stroke="#b04309"
                  tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}K` : value}
                />
                <Tooltip
                  formatter={(value) => [`${formatPrice(value)} ₺`, 'Kazanç']}
                  labelFormatter={(label) => `${label}`}
                />
                <Line
                  type="monotone"
                  dataKey="earnings"
                  name="Kazanç"
                  stroke="#2e7d32"
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#2e7d32' }}
                  activeDot={{ r: 6, fill: '#1b5e20' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Aylık Net Kazanç Trendi */}
        <div className="stats-section">
          <div className="section-header">
            <h3>Aylık Net Kazanç Trendi ({new Date().getFullYear()})</h3>
          </div>
          <div className="chart-card">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={statsTab === 'retail' ? monthlyNetProfitTrend : dealerMonthlyNetProfitTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                <XAxis dataKey="month" stroke="#b04309" />
                <YAxis
                  stroke="#b04309"
                  tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}K` : value}
                />
                <Tooltip
                  formatter={(value) => [`${formatPrice(value)} ₺`, 'Net Kazanç']}
                  labelFormatter={(label) => `${label}`}
                />
                <Line
                  type="monotone"
                  dataKey="netProfit"
                  name="Net Kazanç"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#10b981' }}
                  activeDot={{ r: 6, fill: '#059669' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gelir İstatistikleri */}
        <div className="stats-section revenue-stats">
          <h3>Gelir İstatistikleri</h3>
          <div className="stats-grid">
            {statsTab === 'retail' && (
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-coins"></i>
                </div>
                <div className="stat-content">
                  <h3>Beklenen Gelir</h3>
                  <div className="stat-number">
                    {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.expectedTotal)} ₺`}
                  </div>
                  <small>Tüm siparişler toplamı</small>
                </div>
              </div>
            )}

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-wallet"></i>
              </div>
              <div className="stat-content">
                <h3>{statsTab === 'retail' ? 'Toplam Gelir' : 'Toplam Sipariş'}</h3>
                <div className="stat-number">
                  {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.total : dealerRevenueStats.totalAmount)} ₺`}
                </div>
                <small>{statsTab === 'retail' ? 'Alınan ödemeler' : 'Tüm sipariş listeleri'}</small>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="stat-content">
                <h3>Toplam Net Gelir</h3>
                <div className="stat-number" style={{ color: '#16a34a' }}>
                  {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.totalNetProfit : dealerRevenueStats.totalNetProfit)} ₺`}
                </div>
                <small>Alınan Ödemeler - Maliyetler</small>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
                <i className="fas fa-hand-holding-usd"></i>
              </div>
              <div className="stat-content">
                <h3>Potansiyel Net Kazanç</h3>
                <div className="stat-number" style={{ color: '#d97706' }}>
                  {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.potentialNetProfit : dealerRevenueStats.potentialNetProfit)} ₺`}
                </div>
                <small>Toplam Satış - Toplam Maliyet</small>
              </div>
            </div>

            {statsTab === 'dealer' && (
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-coins"></i>
                </div>
                <div className="stat-content">
                  <h3>Tahsil Edilen</h3>
                  <div className="stat-number">
                    {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(dealerRevenueStats.paidAmount)} ₺`}
                  </div>
                  <small>Alınan ödemeler</small>
                </div>
              </div>
            )}

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-hourglass-half"></i>
              </div>
              <div className="stat-content">
                <h3>{statsTab === 'retail' ? 'Kalan Ödemeler' : 'Açık Bakiye'}</h3>
                <div className="stat-number">
                  {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.remainingTotal : dealerRevenueStats.remainingAmount)} ₺`}
                </div>
                <small>Tahsil edilecek</small>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-calendar-day"></i>
              </div>
              <div className="stat-content">
                <h3>Bugün</h3>
                <div className="stat-number">
                  {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.today : dealerRevenueStats.today)} ₺`}
                </div>
                <div className="profit-line">
                  <span>Kâr:</span>
                  <span className="profit-amount">{isLoadingStats ? '...' : `${formatPrice(statsTab === 'retail' ? revenueStats.todayProfit : dealerRevenueStats.todayProfit)} ₺`}</span>
                </div>
                <small>Bugünkü gelir</small>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-calendar-week"></i>
              </div>
              <div className="stat-content">
                <h3>Bu Hafta</h3>
                <div className="stat-number">
                  {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.thisWeek : dealerRevenueStats.thisWeek)} ₺`}
                </div>
                <div className="profit-line">
                  <span>Kâr:</span>
                  <span className="profit-amount">{isLoadingStats ? '...' : `${formatPrice(statsTab === 'retail' ? revenueStats.thisWeekProfit : dealerRevenueStats.thisWeekProfit)} ₺`}</span>
                </div>
                <small>Son 7 gün</small>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-calendar-alt"></i>
              </div>
              <div className="stat-content">
                <h3>Bu Ay</h3>
                <div className="stat-number">
                  {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.thisMonth : dealerRevenueStats.thisMonth)} ₺`}
                </div>
                <div className="profit-line">
                  <span>Kâr:</span>
                  <span className="profit-amount">{isLoadingStats ? '...' : `${formatPrice(statsTab === 'retail' ? revenueStats.thisMonthProfit : dealerRevenueStats.thisMonthProfit)} ₺`}</span>
                </div>
                <small>Bu ayki gelir</small>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-calendar-check"></i>
              </div>
              <div className="stat-content">
                <h3>Bu Yıl</h3>
                <div className="stat-number">
                  {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.thisYear : dealerRevenueStats.thisYear)} ₺`}
                </div>
                <div className="profit-line">
                  <span>Kâr:</span>
                  <span className="profit-amount">{isLoadingStats ? '...' : `${formatPrice(statsTab === 'retail' ? revenueStats.thisYearProfit : dealerRevenueStats.thisYearProfit)} ₺`}</span>
                </div>
                <small>Bu yılki gelir</small>
              </div>
            </div>
          </div>

          {/* Tedarikçi Alımları & Nakit Akışı Durumu (Sadece Bayi İçin Gosterilir) */}
          {statsTab === 'dealer' && (
            <>
              <h4 className="payment-type-title" style={{ marginTop: '40px' }}>
                <i className="fas fa-truck-loading"></i> Tedarikçi Alımları & Nakit Akışı Durumu
              </h4>
              <div className="stats-grid payment-type-stats" style={{ marginTop: '15px' }}>
                <div className="stat-card" style={{ borderLeft: '4px solid #ef4444' }}>
                  <div className="stat-icon" style={{ background: '#fee2e2', color: '#ef4444' }}>
                    <i className="fas fa-hand-holding-usd"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Tedarikçiye Borcumuz</h3>
                    <div className="stat-number" style={{ color: '#ef4444' }}>
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(dealerRevenueStats.supplierTotalDebt || 0)} ₺`}
                    </div>
                    <small>Tedarikçilere kalan toplam borcumuz (TL karşılığı)</small>
                  </div>
                </div>

                <div className="stat-card" style={{ borderLeft: '4px solid #3b82f6' }}>
                  <div className="stat-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
                    <i className="fas fa-shopping-basket"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Toplam Alım Hacmi</h3>
                    <div className="stat-number" style={{ color: '#3b82f6' }}>
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(dealerRevenueStats.supplierTotalPurchases || 0)} ₺`}
                    </div>
                    <small>Tedarikçilerden yapılan alımların toplamı (TL karşılığı)</small>
                  </div>
                </div>

                <div className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
                  <div className="stat-icon" style={{ background: '#d1fae5', color: '#10b981' }}>
                    <i className="fas fa-check-double"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Tedarikçiye Ödenen</h3>
                    <div className="stat-number" style={{ color: '#10b981' }}>
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(dealerRevenueStats.supplierTotalPaid || 0)} ₺`}
                    </div>
                    <small>Tedarikçilere bugüne dek yapılan ödemeler (TL karşılığı)</small>
                  </div>
                </div>

                <div className="stat-card" style={{
                  borderLeft: '4px solid ' + ((dealerRevenueStats.remainingAmount - (dealerRevenueStats.supplierTotalDebt || 0)) >= 0 ? '#10b981' : '#ef4444')
                }}>
                  <div className="stat-icon" style={{
                    background: (dealerRevenueStats.remainingAmount - (dealerRevenueStats.supplierTotalDebt || 0)) >= 0 ? '#d1fae5' : '#fee2e2',
                    color: (dealerRevenueStats.remainingAmount - (dealerRevenueStats.supplierTotalDebt || 0)) >= 0 ? '#10b981' : '#ef4444'
                  }}>
                    <i className="fas fa-balance-scale"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Net Cari Denge</h3>
                    <div className="stat-number" style={{
                      color: (dealerRevenueStats.remainingAmount - (dealerRevenueStats.supplierTotalDebt || 0)) >= 0 ? '#10b981' : '#ef4444'
                    }}>
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(dealerRevenueStats.remainingAmount - (dealerRevenueStats.supplierTotalDebt || 0))} ₺`}
                    </div>
                    <small>Bayi Alacaklarımız − Tedarikçi Net Bakiyemiz (borç/alacak dahil)</small>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Ödeme Tipine Göre Günlük Gelirler (Sadece Perakende İçin Gosterilir) */}
          {statsTab === 'retail' && (
            <>
              <h4 className="payment-type-title"><i className="fas fa-receipt"></i> Ödeme Tipine Göre</h4>
              <div className="stats-grid payment-type-stats">
                <div className="stat-card payment-cash">
                  <div className="stat-icon">
                    <i className="fas fa-money-bill-wave"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Bugün (Nakit)</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.todayCash)} ₺`}
                    </div>
                    <small>Elden nakit ödemeler</small>
                  </div>
                </div>
                <div className="stat-card payment-iban">
                  <div className="stat-icon">
                    <i className="fas fa-university"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Bugün (IBAN)</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.todayIban)} ₺`}
                    </div>
                    <small>Banka transferi</small>
                  </div>
                </div>
                <div className="stat-card payment-credit-card">
                  <div className="stat-icon">
                    <i className="fas fa-credit-card"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Dün (Kredi Kartı)</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.yesterdayCreditCard)} ₺`}
                    </div>
                    <small>Bir gün gecikmeyle yansır</small>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Geçen Yıl Karşılaştırması (Sadece Perakende İçin Gosterilir) */}
        {statsTab === 'retail' && (
          <div className="stats-section">
            <h3>Geçen Yıl Karşılaştırması ({new Date().getFullYear() - 1})</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-history"></i>
                </div>
                <div className="stat-content">
                  <h3>Geçen Yıl Bugün</h3>
                  <div className="stat-number">
                    {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.today}
                  </div>
                  <small>Aynı gün müşteri sayısı</small>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-history"></i>
                </div>
                <div className="stat-content">
                  <h3>Geçen Yıl Bu Hafta</h3>
                  <div className="stat-number">
                    {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.thisWeek}
                  </div>
                  <small>Aynı hafta müşteri sayısı</small>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-history"></i>
                </div>
                <div className="stat-content">
                  <h3>Geçen Yıl Bu Ay</h3>
                  <div className="stat-number">
                    {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.thisMonth}
                  </div>
                  <small>Aynı ay müşteri sayısı</small>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-chart-bar"></i>
                </div>
                <div className="stat-content">
                  <h3>Geçen Yıl Toplam</h3>
                  <div className="stat-number">
                    {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.total}
                  </div>
                  <small>Yıl boyunca toplam</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Backup/Restore Bölümü */}
        <div className="backup-section">
          <h3>Veri Yönetimi</h3>
          <div className="backup-grid">
            <div className="backup-card">
              <div className="backup-icon">📥</div>
              <h4>Yedek Al</h4>
              <p>Tüm müşteri ve sipariş verilerinizi JSON formatında yedekleyin</p>
              <button
                onClick={handleExport}
                disabled={isExporting || isLoadingStats}
                className="backup-btn export-btn"
              >
                {isExporting ? (
                  <span>
                    <div className="btn-spinner"></div>
                    Yedekleniyor...
                  </span>
                ) : (
                  <>
                    <i className="fas fa-download"></i>
                    Yedek Al
                  </>
                )}
              </button>
            </div>

            <div className="backup-card">
              <div className="backup-icon">📤</div>
              <h4>Yedek Yükle</h4>
              <p>Daha önce aldığınız backup dosyasını geri yükleyin</p>
              <button
                onClick={handleImportClick}
                disabled={isImporting || isLoadingStats}
                className="backup-btn import-btn"
              >
                <i className="fas fa-upload"></i>
                Yedek Yükle
              </button>
            </div>
          </div>

          <div className="backup-info">
            <i className="fas fa-info-circle"></i>
            <p>
              <strong>Önemli:</strong> Yedek yükleme işlemi mevcut verilerinizi silmez.
              Aynı ID'ye sahip veriler güncellenir, yeni veriler eklenir.
            </p>
          </div>
        </div>

        {/* Firma Bilgileri Bölümü */}
        <div className="company-info-section">
          <div className="company-info-header">
            <h3>
              <i className="fas fa-building"></i>
              Firma Bilgileri
            </h3>
            <button
              className="btn-toggle-company-info"
              onClick={() => {
                setShowCompanyInfo(!showCompanyInfo);
                setCompanyError(null);
                setCompanySuccess(false);
                if (!showCompanyInfo) {
                  handleCancelCompany();
                }
              }}
            >
              {showCompanyInfo ? (
                <>
                  <i className="fas fa-chevron-up"></i>
                  Gizle
                </>
              ) : (
                <>
                  <i className="fas fa-chevron-down"></i>
                  Göster
                </>
              )}
            </button>
          </div>

          {showCompanyInfo && (
            <div className="company-info-content">
              {companyError && (
                <div className="company-message error">
                  <i className="fas fa-exclamation-circle"></i>
                  {companyError}
                </div>
              )}

              {companySuccess && (
                <div className="company-message success">
                  <i className="fas fa-check-circle"></i>
                  {companySuccess}
                </div>
              )}

              <form onSubmit={handleSaveCompany} className="company-form">
                <div className="form-group">
                  <label htmlFor="companyName">
                    <i className="fas fa-building"></i>
                    Firma Adı *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    value={companyForm.name}
                    onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                    placeholder="Firma adını girin"
                    required
                    disabled={isLoadingCompanies}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="companyPhone">
                    <i className="fas fa-phone"></i>
                    Telefon Numarası *
                  </label>
                  <input
                    type="text"
                    id="companyPhone"
                    value={companyForm.phone}
                    onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                    placeholder="905321234567 veya +905321234567"
                    required
                    disabled={isLoadingCompanies}
                  />
                  <small>Ülke kodu ile birlikte girin (örn: 905321234567)</small>
                </div>

                <div className="company-form-actions">
                  <button
                    type="submit"
                    className="btn-save-company"
                    disabled={isLoadingCompanies}
                  >
                    {isLoadingCompanies ? (
                      <>
                        <div className="btn-spinner"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        {editingCompany ? 'Güncelle' : 'Ekle'}
                      </>
                    )}
                  </button>
                  {editingCompany && (
                    <button
                      type="button"
                      className="btn-cancel-company"
                      onClick={handleCancelCompany}
                      disabled={isLoadingCompanies}
                    >
                      İptal
                    </button>
                  )}
                </div>
              </form>

              {/* Firma Listesi */}
              <div className="companies-list">
                <h4>Kayıtlı Firmalar</h4>
                {isLoadingCompanies ? (
                  <div className="loading-companies">
                    <div className="stat-spinner"></div>
                    <span>Yükleniyor...</span>
                  </div>
                ) : companies.length === 0 ? (
                  <div className="no-companies">
                    <p>Henüz firma eklenmemiş.</p>
                  </div>
                ) : (
                  <div className="companies-table-wrapper">
                    <table className="companies-table">
                      <thead>
                        <tr>
                          <th>Firma Adı</th>
                          <th>Telefon</th>
                          <th>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companies.map((company) => (
                          <tr key={company.id}>
                            <td>{company.name}</td>
                            <td>{company.phone}</td>
                            <td>
                              <div className="company-actions">
                                <button
                                  onClick={() => handleEditCompany(company)}
                                  className="edit-company-btn"
                                  disabled={isLoadingCompanies}
                                  title="Düzenle"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  onClick={() => handleDeleteCompanyClick(company)}
                                  className="delete-company-btn"
                                  disabled={isLoadingCompanies}
                                  title="Sil"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="company-info-info">
                <i className="fas fa-info-circle"></i>
                <div>
                  <strong>Bilgi:</strong>
                  <ul>
                    <li>Eklediğiniz firmalar WhatsApp mesaj gönderme bölümünde görünecektir.</li>
                    <li>Telefon numarasını ülke kodu ile birlikte girin (örn: 905321234567).</li>
                    <li>Firma adı benzersiz olmalıdır.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Şifre Değiştirme Bölümü */}
        <div className="password-change-section">
          <div className="password-change-header">
            <h3>
              <i className="fas fa-key"></i>
              Şifre Değiştir
            </h3>
            <button
              className="btn-toggle-password-change"
              onClick={() => {
                setShowPasswordChange(!showPasswordChange);
                setPasswordError('');
                setPasswordSuccess('');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
              }}
            >
              {showPasswordChange ? (
                <>
                  <i className="fas fa-chevron-up"></i>
                  Gizle
                </>
              ) : (
                <>
                  <i className="fas fa-chevron-down"></i>
                  Göster
                </>
              )}
            </button>
          </div>

          {showPasswordChange && (
            <div className="password-change-content">
              {passwordError && (
                <div className="password-message error">
                  <i className="fas fa-exclamation-circle"></i>
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div className="password-message success">
                  <i className="fas fa-check-circle"></i>
                  {passwordSuccess}
                </div>
              )}

              <form onSubmit={handlePasswordFormSubmit} className="password-change-form">
                <div className="form-group">
                  <label htmlFor="currentPassword">
                    <i className="fas fa-lock"></i>
                    Mevcut Şifre *
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Mevcut şifrenizi girin"
                    required
                    disabled={isChangingPassword}
                    autoComplete="current-password"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">
                    <i className="fas fa-key"></i>
                    Yeni Şifre *
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Yeni şifrenizi girin (en az 6 karakter)"
                    required
                    disabled={isChangingPassword}
                    autoComplete="new-password"
                    minLength={6}
                  />
                  <small>En az 6 karakter olmalıdır</small>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">
                    <i className="fas fa-key"></i>
                    Yeni Şifre Tekrar *
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Yeni şifrenizi tekrar girin"
                    required
                    disabled={isChangingPassword}
                    autoComplete="new-password"
                    minLength={6}
                  />
                </div>

                <div className="password-change-actions">
                  <button
                    type="submit"
                    className="btn-change-password"
                    disabled={isChangingPassword}
                  >
                    {isChangingPassword ? (
                      <>
                        <div className="btn-spinner"></div>
                        Değiştiriliyor...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        Şifreyi Değiştir
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn-cancel-password"
                    onClick={() => {
                      setShowPasswordChange(false);
                      setPasswordError('');
                      setPasswordSuccess('');
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    disabled={isChangingPassword}
                  >
                    İptal
                  </button>
                </div>
              </form>

              <div className="password-change-info">
                <i className="fas fa-info-circle"></i>
                <div>
                  <strong>Güvenlik Notları:</strong>
                  <ul>
                    <li>Şifreniz en az 6 karakter olmalıdır.</li>
                    <li>Güvenli bir şifre için büyük/küçük harf, rakam ve özel karakter kullanın.</li>
                    <li>Şifre değiştirdikten sonra oturumunuz açık kalacaktır.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#fcfcfc' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="stat-spinner" style={{ width: '40px', height: '40px', borderWidth: '4px', margin: '0 auto 15px' }}></div>
          <h2 style={{ color: '#555', fontSize: '18px', fontWeight: '500' }}>Yetkiler Kontrol Ediliyor...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-top">
          <h1>Evax Perde - Perakende ve Toptan Müşteri Listesi</h1>
          <div className="header-actions">
            {user?.email && (
              <span className="user-email">{user.email}</span>
            )}
            <button onClick={handleLogout} className="logout-button">
              Çıkış Yap
            </button>
          </div>
        </div>

        <div className="header-bottom">
          <nav className="dashboard-nav">
            {/* Ana Sayfa - Satışcı hariç herkes */}
            {permissions?.canAccessDashboard && (
              <button
                className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setActiveTab('dashboard')}
              >
                Ana Sayfa
              </button>
            )}

            {/* Müşteri Listesi - Herkes (Montajcı sadece montaj durumundakiler) */}
            <button
              className={`nav-btn ${activeTab === 'customers' ? 'active' : ''}`}
              onClick={() => setActiveTab('customers')}
            >
              {isMontajci ? 'Montaj Listesi' : 'Müşteri Listesi'}
            </button>

            {/* Bayi Listesi - Sadece admin vb. ya da montajcı hariç herkes */}
            {!isMontajci && (
              <button
                className={`nav-btn ${activeTab === 'dealers' ? 'active' : ''}`}
                onClick={() => setActiveTab('dealers')}
              >
                <i className="fas fa-store"></i> Bayi Listesi
              </button>
            )}

            {/* Kullanıcı Yönetimi - Sadece Admin */}
            {isAdmin && (
              <button
                className={`nav-btn ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <i className="fas fa-users-cog"></i> Kullanıcılar
              </button>
            )}

            {/* Ürün Listesi - Montajcı hariç herkes */}
            {!isMontajci && (
              <button
                className={`nav-btn ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => setActiveTab('products')}
              >
                <i className="fas fa-tags"></i> Ürün Listesi
              </button>
            )}

            {/* GEÇİCİ DEVRE DIŞI - Hesaplamalar sekmesi kaldırıldı
            {isAdmin && (
              <button
                className={`nav-btn ${activeTab === 'accounting' ? 'active' : ''}`}
                onClick={() => setActiveTab('accounting')}
              >
                <i className="fas fa-calculator"></i> Hesaplamalar
              </button>
            )}
            */}
          </nav>
        </div>
      </header>

      <main className="dashboard-main">
        {/* Dashboard Tab */}
        <div style={{ display: activeTab === 'dashboard' ? 'block' : 'none' }}>
          {!permissions?.canAccessDashboard ? (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Dashboard'a erişim yetkiniz bulunmamaktadır.</p>
              <p>Müşteri Listesi sekmesinden işlemlerinizi yapabilirsiniz.</p>
            </div>
          ) : (
            renderDashboardContent()
          )}
        </div>

        {/* Müşteri Listesi (Customers) Tab */}
        {hasMountedCustomers && (
          <div style={{ display: activeTab === 'customers' ? 'block' : 'none' }}>
            <CustomerList onCustomerChange={handleCustomerChange} userRole={userRole} userId={user?.uid} />
          </div>
        )}

        {/* Bayi Listesi (Dealers) Tab */}
        {hasMountedDealers && (
          <div style={{ display: activeTab === 'dealers' ? 'block' : 'none' }}>
            <DealerList onCustomerChange={handleCustomerChange} />
          </div>
        )}

        {/* Ürün Listesi (Products) Tab */}
        {hasMountedProducts && (
          <div style={{ display: activeTab === 'products' ? 'block' : 'none' }}>
            {isMontajci ? (
              <div className="access-denied-card">
                <i className="fas fa-lock"></i>
                <h2>Erişim Reddedildi</h2>
                <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
              </div>
            ) : (
              <ProductList />
            )}
          </div>
        )}

        {/* Kullanıcı Yönetimi (Users) Tab */}
        {hasMountedUsers && (
          <div style={{ display: activeTab === 'users' ? 'block' : 'none' }}>
            {!isAdmin ? (
              <div className="access-denied-card">
                <i className="fas fa-lock"></i>
                <h2>Erişim Reddedildi</h2>
                <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
              </div>
            ) : (
              renderUserManagement()
            )}
          </div>
        )}
      </main>

      <footer className="dashboard-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <i className="fas fa-store"></i>
            <span>{APP_NAME}</span>
          </div>

          <div className="footer-center">
            <span className="footer-dev-label">Developed by</span>
            <a
              href="https://www.kahramantech.com"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-dev-link"
            >
              <i className="fas fa-code footer-dev-icon"></i>
              <span>KahramanTech</span>
              <i className="fas fa-external-link-alt footer-ext-icon"></i>
            </a>
          </div>

          <div className="footer-right">
            <span className="footer-copyright">© {new Date().getFullYear()} Tüm hakları saklıdır.</span>
            <span className="footer-sep">·</span>
            <span className="version-badge">v{APP_VERSION}</span>
          </div>
        </div>
      </footer>

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal-content import-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-upload"></i>
                Yedek Dosyası Yükle
              </h3>
              <button onClick={closeImportModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="file-upload-area">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  id="backup-file-input"
                  className="file-input"
                />
                <label htmlFor="backup-file-input" className="file-upload-label">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>JSON dosyası seçin veya sürükleyin</span>
                </label>

                {importFile && (
                  <div className="selected-file">
                    <i className="fas fa-file-alt"></i>
                    <span>{importFile.name}</span>
                    <small>({(importFile.size / 1024).toFixed(1)} KB)</small>
                  </div>
                )}
              </div>

              <div className="import-warning">
                <i className="fas fa-exclamation-triangle"></i>
                <div>
                  <strong>Dikkat:</strong>
                  <ul>
                    <li>Backup dosyası JSON formatında olmalıdır</li>
                    <li>Mevcut verileriniz korunur, silinmez</li>
                    <li>Aynı ID'ye sahip veriler güncellenir</li>
                    <li>İşlem geri alınamaz, önceden yedek aldığınızdan emin olun</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleImport}
                disabled={!importFile || isImporting}
                className="btn-primary import-confirm-btn"
              >
                {isImporting ? (
                  <span>
                    <div className="btn-spinner"></div>
                    Yükleniyor...
                  </span>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    Yedek Yükle
                  </>
                )}
              </button>
              <button
                onClick={closeImportModal}
                disabled={isImporting}
                className="btn-secondary"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notificationModal.show && (
        <div className="modal-overlay">
          <div className={`modal-content notification-modal ${notificationModal.type}`}>
            <div className="modal-header">
              <h3>
                {notificationModal.type === 'success' && <i className="fas fa-check-circle"></i>}
                {notificationModal.type === 'error' && <i className="fas fa-times-circle"></i>}
                {notificationModal.type === 'warning' && <i className="fas fa-exclamation-triangle"></i>}
                {notificationModal.type === 'info' && <i className="fas fa-info-circle"></i>}
                {notificationModal.title}
              </h3>
              <button onClick={closeNotificationModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p className="notification-message">{notificationModal.message}</p>

              {/* Export detayları */}
              {notificationModal.details && notificationModal.details.customers !== undefined && !notificationModal.details.backupCustomers && (
                <div className="notification-details">
                  <div className="detail-item">
                    <i className="fas fa-users"></i>
                    <span>{notificationModal.details.customers} müşteri</span>
                  </div>
                  <div className="detail-item">
                    <i className="fas fa-shopping-cart"></i>
                    <span>{notificationModal.details.orders} sipariş</span>
                  </div>
                </div>
              )}

              {/* Import detayları */}
              {notificationModal.details && notificationModal.details.backupCustomers !== undefined && (
                <div className="notification-details import-details">
                  <div className="detail-section">
                    <h4>📊 Yedek Dosyası</h4>
                    <div className="detail-row">
                      <span>{notificationModal.details.backupCustomers} müşteri</span>
                      <span>{notificationModal.details.backupOrders} sipariş</span>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>✅ İşlem Sonucu</h4>
                    <div className="detail-grid">
                      <div className="detail-item success">
                        <span className="detail-label">Yeni Müşteri</span>
                        <span className="detail-value">{notificationModal.details.addedCustomers}</span>
                      </div>
                      <div className="detail-item info">
                        <span className="detail-label">Güncellenen Müşteri</span>
                        <span className="detail-value">{notificationModal.details.updatedCustomers}</span>
                      </div>
                      <div className="detail-item success">
                        <span className="detail-label">Yeni Sipariş</span>
                        <span className="detail-value">{notificationModal.details.addedOrders}</span>
                      </div>
                      <div className="detail-item info">
                        <span className="detail-label">Güncellenen Sipariş</span>
                        <span className="detail-value">{notificationModal.details.updatedOrders}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={closeNotificationModal} className="btn-primary">
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Firma Silme Onay Modal */}
      {deleteCompanyModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-company-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle"></i>
                Firma Sil
              </h3>
              <button onClick={handleCloseDeleteCompanyModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>
                <strong>{deleteCompanyModal.name}</strong> adlı firmayı silmek istediğinizden emin misiniz?
              </p>
              <p className="warning-text">
                <i className="fas fa-exclamation-circle"></i>
                Bu işlem geri alınamaz!
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleDeleteCompany}
                className="btn-primary delete-confirm-btn"
                disabled={isLoadingCompanies}
              >
                {isLoadingCompanies ? (
                  <>
                    <div className="btn-spinner"></div>
                    Siliniyor...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash"></i>
                    Evet, Sil
                  </>
                )}
              </button>
              <button
                onClick={handleCloseDeleteCompanyModal}
                className="btn-secondary"
                disabled={isLoadingCompanies}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Kullanıcı Ekleme Modal */}
      {showAddUserModal && (
        <div className="modal-overlay">
          <div className="modal-content add-user-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-user-plus"></i>
                Yeni Kullanıcı Oluştur
              </h3>
              <button onClick={() => {
                setShowAddUserModal(false);
                setNewUserForm({ email: '', password: '', displayName: '', role: ROLES.SATISCI });
                setUserFormError(null);
              }} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleCreateUser}>
              <div className="modal-body">
                {userFormError && (
                  <div className="form-error">
                    <i className="fas fa-exclamation-circle"></i>
                    {userFormError}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="displayName">
                    <i className="fas fa-user"></i>
                    Kullanıcı Adı
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    value={newUserForm.displayName}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, displayName: e.target.value }))}
                    placeholder="Örn: Ahmet Yılmaz"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <i className="fas fa-envelope"></i>
                    E-posta
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="ornek@email.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    <i className="fas fa-lock"></i>
                    Şifre
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="En az 6 karakter"
                    minLength={6}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role">
                    <i className="fas fa-user-tag"></i>
                    Rol
                  </label>
                  <select
                    id="role"
                    value={newUserForm.role}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <option value={ROLES.SATISCI}>Satışcı</option>
                    <option value={ROLES.MONTAJCI}>Montajcı</option>
                    <option value={ROLES.YAZILIMCI}>Yazılımcı</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="submit" className="btn-primary">
                  <i className="fas fa-user-plus"></i>
                  Kullanıcı Oluştur
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUserModal(false);
                    setNewUserForm({ email: '', password: '', displayName: '', role: ROLES.SATISCI });
                    setUserFormError(null);
                  }}
                  className="btn-secondary"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Kullanıcı Silme Onay Modal */}
      {deleteUserModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-user-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle"></i>
                Kullanıcı Sil
              </h3>
              <button onClick={() => setDeleteUserModal(null)} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>
                <strong>{deleteUserModal.displayName || deleteUserModal.email}</strong> kullanıcısını silmek istediğinizden emin misiniz?
              </p>
              <p className="warning-text">
                <i className="fas fa-exclamation-circle"></i>
                Bu işlem geri alınamaz! Kullanıcı sisteme giriş yapamayacak. Bu Gmail bilgisi ile tekrar bir kullanıcı oluşturamayacaksınız, kullanıcı tekrar bu bilgilerle sisteme erişemeyecek.
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => handleDeleteUser(deleteUserModal.id)}
                className="btn-primary delete-confirm-btn"
              >
                <i className="fas fa-trash"></i>
                Evet, Sil
              </button>
              <button
                onClick={() => setDeleteUserModal(null)}
                className="btn-secondary"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Şifre Değiştirme Onay Modal */}
      {showPasswordConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-user-modal">
            <div className="modal-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fas fa-exclamation-triangle" style={{ color: '#f39c12', fontSize: '1.3rem' }}></i>
                Güvenlik Uyarısı
              </h3>
              <button onClick={() => setShowPasswordConfirmModal(false)} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p style={{ fontSize: '1rem', lineHeight: '1.5', marginBottom: '15px' }}>
                <strong>Şifrenizi değiştirmek üzeresiniz.</strong>
              </p>
              <div className="warning-text" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: 'rgba(243, 156, 18, 0.1)', borderLeft: '4px solid #f39c12', color: 'inherit', padding: '12px' }}>
                <i className="fas fa-info-circle" style={{ color: '#f39c12', fontSize: '1.2rem', marginTop: '2px' }}></i>
                <div>
                  <strong style={{ display: 'block', marginBottom: '4px' }}>Önemli Bilgi:</strong>
                  Şifreniz güncellendiğinde, güvenlik nedeniyle <strong>diğer tüm cihazlardaki aktif oturumlarınız otomatik olarak sonlandırılacaktır.</strong>
                  <br />
                  Devam etmek istiyor musunuz?
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleChangePasswordConfirm}
                className="btn-primary"
                style={{ background: 'linear-gradient(135deg, var(--primary-500) 0%, var(--primary-600) 100%)', borderColor: 'transparent' }}
              >
                <i className="fas fa-check"></i>
                Evet, Değiştir
              </button>
              <button
                onClick={() => setShowPasswordConfirmModal(false)}
                className="btn-secondary"
              >
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;